/**
 * Partner Directory - Seite /pd
 *
 * Kennungen (Pid), Parameter und Ermittlungsregeln einer Instanz einsehen und pflegen.
 *
 * Fachliche Grundlage: Eine Pid ist KEINE eigene Entitaet. Sie entsteht implizit mit dem
 * ersten Parameter und verschwindet mit dem letzten. "Eintrag anlegen" heisst deshalb:
 * Kennung plus mindestens ein Parameter. Beim Loeschen des letzten Inhalts sagt die
 * Rueckfrage genau das.
 *
 * Design: Integration-in-One Tokens, alle Klassen pd-* gescoped unter .pd-root.
 * Kein MUI, keine Etiketten, Modals nativ als .iio-modal.
 */
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useTranslation, Trans } from "react-i18next";
import i18n from "../i18n";
import { api } from "../utils/helpers";
import { istProdNah } from "../utils/stages";
import PartnerDirectoryEdgeSync from "../components/PartnerDirectoryEdgeSync";

const PD_CSS = `
/* Elevation-Grammatik: Sektion = Arbeitsflaeche (shadow-sm), Informatives = Hairline. */
.pd-root{ color:var(--iio-text-1);
  --pd-raise:var(--iio-bg-2); --pd-raise-border:var(--iio-border);
  --pd-h:clamp(420px, calc(100vh - 268px), 940px); }
[data-theme="dark"] .pd-root{ --pd-raise:var(--iio-bg-3); --pd-raise-border:var(--iio-border-subtle); }

.pd-ph{ margin-bottom:var(--iio-s-4); }
.pd-ph .crumb{ font-size:11px; letter-spacing:.04em; color:var(--iio-text-3); margin-bottom:7px; }
.pd-ph .crumb b{ color:var(--iio-signal); font-weight:700; }
.pd-ph h1{ margin:0; font-size:27px; font-weight:700; letter-spacing:-.02em; }
.pd-ph .sub{ margin-top:6px; font-size:13.5px; color:var(--iio-text-2); max-width:720px; line-height:1.5; }

.pd-ctx{ display:flex; align-items:flex-end; gap:var(--iio-s-4); flex-wrap:wrap;
  padding:12px 16px; margin-bottom:var(--iio-s-4);
  background:var(--pd-raise); border:1px solid var(--pd-raise-border);
  border-radius:var(--iio-r-lg); box-shadow:var(--iio-shadow-sm); }
.pd-ctx > *{ min-width:0; }
.pd-field{ display:flex; flex-direction:column; gap:6px; min-width:240px; }
.pd-field label{ font-size:10.5px; font-weight:600; letter-spacing:.1em;
  text-transform:uppercase; color:var(--iio-text-3); }
.pd-lbl{ font-size:10.5px; font-weight:600; letter-spacing:.1em;
  text-transform:uppercase; color:var(--iio-text-3); }
/* Der Wahlknopf schreibt die Wahl aus: Space klein darueber, Instanz gross darunter.
   Zwei Zeilen statt "Space / Instanz" in einer - die Frage "wo bin ich gerade" wird damit
   auf einen Blick beantwortet, ohne den Text zu lesen. */
.pd-instbtn{ display:flex; align-items:center; gap:12px; min-height:46px; width:100%;
  padding:6px 12px; border:1px solid var(--iio-border); border-radius:var(--iio-r-sm);
  background:var(--iio-bg-1); font-family:inherit; text-align:left; cursor:pointer; outline:none; }
.pd-instbtn:hover{ border-color:var(--iio-text-4); }
.pd-instbtn:focus-visible{ border-color:var(--iio-brand-ink); box-shadow:0 0 0 3px rgba(10,61,74,.12); }
[data-theme="dark"] .pd-root .pd-instbtn:focus-visible{ box-shadow:0 0 0 3px rgba(90,154,224,.18); }
.pd-instbtn .txt{ display:flex; flex-direction:column; gap:1px; min-width:0; flex:1; }
.pd-instbtn .sp{ font-size:11px; color:var(--iio-text-3); overflow:hidden;
  text-overflow:ellipsis; white-space:nowrap; }
.pd-instbtn .nm{ font-size:14px; font-weight:600; color:var(--iio-text-1); overflow:hidden;
  text-overflow:ellipsis; white-space:nowrap; }
.pd-instbtn .nm.leer{ font-weight:400; color:var(--iio-text-3); }
.pd-instbtn .wechseln{ font-size:11.5px; color:var(--iio-text-3); flex-shrink:0; }
.pd-instbtn:hover .wechseln{ color:var(--iio-text-1); }
.pd-instbtn > svg{ width:14px; height:14px; flex-shrink:0; fill:none; stroke:var(--iio-text-3);
  stroke-width:2; stroke-linecap:round; }
.pd-instbtn.gewaehlt{ border-color:var(--iio-border); }

/* Popup: Spaces als Gruppen, Instanzen darunter. */
.iio-modal.pd-iw{ width:520px; max-width:94vw; }
.pd-iw-body{ display:flex; flex-direction:column; gap:2px; max-height:60vh; overflow:auto; }
.pd-iw-suche{ height:34px; padding:0 10px; margin-bottom:8px; border:1px solid var(--iio-border);
  border-radius:var(--iio-r-sm); background:var(--iio-bg-1); color:var(--iio-text-1);
  font-family:inherit; font-size:13px; outline:none; }
.pd-iw-suche:focus{ border-color:var(--iio-brand-ink); }
.pd-iw-grp + .pd-iw-grp{ margin-top:12px; }
.pd-iw-sp{ font-size:10.5px; font-weight:600; letter-spacing:.1em; text-transform:uppercase;
  color:var(--iio-text-3); padding:6px 2px 5px; border-bottom:1px solid var(--iio-border-subtle);
  margin-bottom:2px; }
.pd-iw-item{ display:flex; align-items:center; gap:10px; width:100%; padding:9px 10px;
  border:none; border-radius:var(--iio-r-sm); background:none; font-family:inherit;
  text-align:left; cursor:pointer; }
.pd-iw-item:hover{ background:var(--iio-bg-2); }
.pd-iw-item:focus-visible{ outline:2px solid var(--iio-brand-ink); outline-offset:-2px; }
.pd-iw-item .nm{ font-size:13.5px; color:var(--iio-text-1); flex:1; min-width:0;
  overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.pd-iw-item .st{ font-size:10.5px; letter-spacing:.06em; color:var(--iio-text-3); flex-shrink:0; }
.pd-iw-item .st.prod{ color:var(--iio-signal); font-weight:600; }
.pd-iw-item .hk{ font-size:11px; color:var(--iio-signal); flex-shrink:0; }
.pd-iw-item.an{ background:var(--iio-bg-2); box-shadow:inset 2px 0 0 var(--iio-signal); }

/* Bestandszeile: informativ, deshalb Hairline-Grammatik statt Kasten. */
.pd-ctx-count{ font-size:12.5px; color:var(--iio-text-3); padding-bottom:10px;
  display:flex; align-items:center; gap:8px; }
.pd-ctx-count b{ color:var(--iio-text-1); font-weight:600; font-variant-numeric:tabular-nums; }
.pd-ctx-count .sep{ width:1px; height:13px; background:var(--iio-border); }
.pd-ctx-sp{ margin-left:auto; }
.pd-ctx-ro{ font-size:12px; color:var(--iio-text-3); padding-bottom:10px; }

/* Buttons */
.pd-btn{ height:38px; padding:0 18px; border:1px solid transparent; border-radius:var(--iio-r-md);
  font-family:inherit; font-size:13px; font-weight:700; cursor:pointer; white-space:nowrap;
  display:inline-flex; align-items:center; justify-content:center; gap:8px;
  transition:all var(--iio-tx-fast,120ms ease); }
.pd-btn.prim{ background:var(--iio-signal); color:#fff; }
.pd-btn.prim:hover:not(:disabled){ background:var(--iio-signal-2); }
.pd-btn.ghost{ background:transparent; color:var(--iio-text-2); border-color:var(--iio-border); }
.pd-btn.ghost:hover:not(:disabled){ background:var(--iio-bg-3); color:var(--iio-text-1); }
.pd-btn.danger{ background:transparent; color:var(--iio-critical); border-color:var(--iio-border); }
.pd-btn.danger:hover:not(:disabled){ background:color-mix(in srgb,var(--iio-critical) 9%,transparent);
  border-color:var(--iio-critical); }
.pd-btn.sm{ height:30px; padding:0 12px; font-size:12px; }
.pd-btn:disabled{ opacity:.5; cursor:not-allowed; }
.pd-btn svg{ width:16px; height:16px; stroke:currentColor; fill:none; stroke-width:2;
  stroke-linecap:round; stroke-linejoin:round; }
.pd-btn.sm svg{ width:13px; height:13px; }
.pd-btn:focus-visible{ outline:2px solid var(--iio-info); outline-offset:2px; }

/* Zwei-Spalten-Rahmen */
.pd-split{ display:grid; grid-template-columns:300px minmax(0,1fr); gap:var(--iio-s-4);
  align-items:start; }
@media(max-width:900px){ .pd-split{ grid-template-columns:1fr; } }
.pd-split > *{ min-width:0; }

/* Liste */
.pd-list{ display:flex; flex-direction:column; height:var(--pd-h);
  background:var(--pd-raise); border:1px solid var(--pd-raise-border);
  border-radius:var(--iio-r-lg); box-shadow:var(--iio-shadow-sm); overflow:hidden; }
.pd-list > *{ flex-shrink:0; }
.pd-search{ position:relative; display:flex; align-items:center; padding:10px 12px;
  border-bottom:1px solid var(--iio-border-subtle);
  background:color-mix(in srgb, var(--iio-bg-0) 35%, var(--pd-raise)); }
.pd-search svg{ position:absolute; left:21px; width:13px; height:13px; stroke:var(--iio-text-4);
  fill:none; stroke-width:2; stroke-linecap:round; pointer-events:none; }
.pd-search input{ width:100%; height:32px; padding:0 10px 0 28px; border:1px solid var(--iio-border);
  border-radius:var(--iio-r-sm); background:var(--iio-bg-1); color:var(--iio-text-1);
  font-family:inherit; font-size:12.5px; outline:none; box-sizing:border-box;
  transition:border-color var(--iio-tx-fast,120ms ease); }
.pd-search input:focus{ border-color:var(--iio-brand-fg); }
.pd-search input::placeholder{ color:var(--iio-text-4); }

.pd-rows{ flex:1 1 auto; min-height:0; overflow-y:auto; }
.pd-row{ display:block; width:100%; text-align:left; padding:9px 13px; cursor:pointer;
  background:transparent; border:none; border-bottom:1px solid var(--iio-border-subtle);
  border-left:2px solid transparent; font-family:inherit;
  transition:background var(--iio-tx-fast,120ms ease), border-color var(--iio-tx-fast,120ms ease); }
.pd-row:hover{ background:var(--iio-bg-3); }
.pd-row:focus-visible{ outline:2px solid var(--iio-info); outline-offset:-2px; }
.pd-row.is-active{ background:color-mix(in srgb, var(--iio-signal) 8%, transparent);
  border-left-color:var(--iio-signal); }
.pd-row .pid{ display:block; font-family:var(--iio-font-mono); font-size:12.5px; font-weight:600;
  color:var(--iio-text-1); letter-spacing:.01em;
  overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.pd-row .inh{ display:block; margin-top:3px; font-size:11px; color:var(--iio-text-3);
  font-variant-numeric:tabular-nums; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
/* Das eine Wort mit Gewicht - ersetzt das fruehere Pipeline-Etikett. */
.pd-row .inh .xslt{ color:var(--iio-text-2); font-weight:600; }
.pd-row .inh .sep{ color:var(--iio-text-4); margin:0 5px; }

.pd-list-empty{ padding:26px 18px; text-align:center; font-size:12.5px; color:var(--iio-text-3);
  line-height:1.55; }
.pd-list-empty b{ display:block; color:var(--iio-text-2); font-size:13px; margin-bottom:3px; }
.pd-skel{ height:44px; margin:6px 10px; border-radius:var(--iio-r-sm); background:var(--iio-bg-3);
  animation:pdShim 1.2s ease-in-out infinite; }
@keyframes pdShim{ 0%,100%{ opacity:.55; } 50%{ opacity:1; } }

/* Detailspalte */
.pd-detail{ display:flex; flex-direction:column; height:var(--pd-h);
  background:var(--pd-raise); border:1px solid var(--pd-raise-border);
  border-radius:var(--iio-r-lg); box-shadow:var(--iio-shadow-sm); overflow:hidden; }
.pd-detail > *{ flex-shrink:0; }
.pd-detail-leer{ flex:1 1 auto; display:flex; flex-direction:column; align-items:center;
  justify-content:center; gap:4px; padding:24px; text-align:center; }
.pd-detail-leer b{ font-size:13.5px; font-weight:600; color:var(--iio-text-2); }
.pd-detail-leer span{ font-size:12.5px; color:var(--iio-text-3); line-height:1.55; max-width:340px; }

.pd-dhead{ padding:14px 18px 12px; border-bottom:1px solid var(--iio-border-subtle);
  background:color-mix(in srgb, var(--iio-bg-0) 35%, var(--pd-raise)); }
.pd-dhead .eyebrow{ font-size:10px; font-weight:600; letter-spacing:.14em; text-transform:uppercase;
  color:var(--iio-text-4); }
/* Sehr lange Kennungen brechen, statt zu kuerzen - hoechstens drei Zeilen. */
.pd-dhead h2{ margin:3px 0 0; font-family:inherit; font-size:16px; font-weight:700;
  color:var(--iio-text-1); letter-spacing:.005em; line-height:1.35; overflow-wrap:anywhere;
  display:-webkit-box; -webkit-line-clamp:3; -webkit-box-orient:vertical; overflow:hidden; }

.pd-tabs{ display:flex; gap:0; padding:0 18px; border-bottom:1px solid var(--iio-border-subtle);
  overflow-x:auto; scrollbar-width:thin; }
.pd-tab{ position:relative; flex:0 0 auto; padding:11px 15px 10px; margin-bottom:-1px;
  background:none; border:none; border-bottom:2px solid transparent; cursor:pointer;
  font-family:inherit; font-size:12.5px; font-weight:500; color:var(--iio-text-3);
  white-space:nowrap; transition:color var(--iio-tx-fast,120ms ease); }
.pd-tab:hover{ color:var(--iio-text-1); }
.pd-tab.is-active{ color:var(--iio-text-1); font-weight:700; border-bottom-color:var(--iio-signal); }
.pd-tab:focus-visible{ outline:2px solid var(--iio-info); outline-offset:-3px; }
.pd-tab .n{ margin-left:7px; font-size:11px; font-weight:600; color:var(--iio-text-4);
  font-variant-numeric:tabular-nums; }
.pd-tab.is-active .n{ color:var(--iio-text-2); }
/* Ungespeicherte Bearbeitung: der Reiter sagt es, damit sie beim Wechsel nicht verloren geht. */
.pd-tab .offen{ position:absolute; top:8px; right:6px; width:5px; height:5px; border-radius:50%;
  background:var(--iio-signal); }

.pd-dbody{ flex:1 1 auto; min-height:0; overflow-y:auto; padding:16px 18px 20px; }

/* Tabellen-Grammatik fuer alle vier Reiter */
.pd-tbl{ border:1px solid var(--iio-border-subtle); border-radius:var(--iio-r-md); overflow:hidden;
  background:var(--iio-bg-1); }
.pd-tr{ display:grid; grid-template-columns:minmax(120px,220px) minmax(0,1fr) auto;
  gap:12px; align-items:center; padding:9px 13px; border-top:1px solid var(--iio-border-subtle);
  font-size:12.5px; }
.pd-tr:first-child{ border-top:none; }
.pd-tr.cols4{ grid-template-columns:minmax(0,1fr) minmax(0,1fr) minmax(0,1fr) auto; }
.pd-tr.cols1{ grid-template-columns:minmax(0,1fr); }
.pd-tr.frage{ grid-template-columns:minmax(0,1fr) auto; gap:10px; }
.pd-tr.head{ background:color-mix(in srgb, var(--iio-bg-0) 45%, var(--iio-bg-1));
  font-size:10px; font-weight:600; letter-spacing:.1em; text-transform:uppercase;
  color:var(--iio-text-3); padding-top:7px; padding-bottom:7px; }
.pd-tr .k{ font-family:var(--iio-font-mono); font-size:11.5px; color:var(--iio-text-2);
  letter-spacing:.01em; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.pd-tr .val{ font-family:var(--iio-font-mono); font-size:12px; color:var(--iio-text-1);
  overflow-wrap:anywhere; }
.pd-tr .val.leer{ color:var(--iio-text-4); font-style:italic; font-family:inherit; }
.pd-tr .frg{ font-size:12.5px; color:var(--iio-text-1); line-height:1.5; }
.pd-tr .frg em{ display:block; font-style:normal; color:var(--iio-text-3); font-size:11.5px;
  margin-top:2px; }
.pd-tr .acts{ display:flex; gap:4px; justify-content:flex-end; align-items:center; }
/* Zeilenwerkzeuge erscheinen bei Hover UND bei Tastatur-Fokus - nie nur bei Hover. */
.pd-tr .acts .pd-ic{ opacity:0; transition:opacity var(--iio-tx-fast,120ms ease); }
.pd-tr:hover .acts .pd-ic, .pd-tr:focus-within .acts .pd-ic{ opacity:1; }
.pd-ic{ width:28px; height:28px; display:inline-flex; align-items:center; justify-content:center;
  border:1px solid transparent; border-radius:var(--iio-r-sm); background:transparent;
  color:var(--iio-text-3); cursor:pointer; padding:0; }
.pd-ic:hover{ background:var(--iio-bg-3); color:var(--iio-text-1); border-color:var(--iio-border); }
.pd-ic.del:hover{ color:var(--iio-critical); border-color:var(--iio-critical); }
.pd-ic:focus-visible{ outline:2px solid var(--iio-info); outline-offset:1px; opacity:1; }
.pd-ic:disabled{ opacity:.4; cursor:not-allowed; }
.pd-ic svg{ width:14px; height:14px; stroke:currentColor; fill:none; stroke-width:2;
  stroke-linecap:round; stroke-linejoin:round; }

/* Inline-Bearbeitung eines String-Werts */
.pd-tr .edit{ width:100%; height:30px; padding:0 9px; border:1px solid var(--iio-brand-fg);
  border-radius:var(--iio-r-sm); background:var(--iio-bg-2); color:var(--iio-text-1);
  font-family:var(--iio-font-mono); font-size:12px; outline:none; box-sizing:border-box; }
.pd-tr .edit:focus{ box-shadow:0 0 0 3px rgba(10,61,74,.12); }
[data-theme="dark"] .pd-root .pd-tr .edit:focus{ box-shadow:0 0 0 3px rgba(90,154,224,.18); }

/* Hinzufuegen: die Stelle, an der das Neue erscheinen wird. */
.pd-add{ display:flex; align-items:center; justify-content:center; gap:8px; width:100%;
  margin-top:9px; padding:11px; border:1.5px dashed var(--iio-border); border-radius:var(--iio-r-md);
  background:color-mix(in srgb, var(--iio-bg-0) 32%, var(--pd-raise));
  font-family:inherit; font-size:12.5px; font-weight:600; color:var(--iio-text-2); cursor:pointer;
  transition:border-color var(--iio-tx-fast,120ms ease), color var(--iio-tx-fast,120ms ease); }
.pd-add:hover{ border-color:var(--iio-signal); color:var(--iio-text-1); }
.pd-add:focus-visible{ outline:2px solid var(--iio-info); outline-offset:2px; }
.pd-add svg{ width:14px; height:14px; stroke:currentColor; fill:none; stroke-width:2.2;
  stroke-linecap:round; }

/* Leerer Reiter */
.pd-empty{ padding:22px 4px 6px; font-size:12.5px; color:var(--iio-text-3); line-height:1.6; }
.pd-empty b{ display:block; color:var(--iio-text-2); font-size:13px; font-weight:600; margin-bottom:3px; }
.pd-fuss{ margin-top:12px; font-size:11.5px; color:var(--iio-text-3); }

/* Meldungszeile: Balken plus Text, kein Farbkasten. */
.pd-msg{ display:flex; align-items:flex-start; gap:10px; padding:9px 0 9px 12px;
  border-left:2px solid var(--iio-text-4); font-size:12.5px; color:var(--iio-text-2);
  line-height:1.55; margin-bottom:12px; }
.pd-msg b{ color:var(--iio-text-1); font-weight:600; }
.pd-msg.warn{ border-left-color:var(--iio-warning); }
.pd-msg.bad{ border-left-color:var(--iio-critical); }
.pd-msg.ok{ border-left-color:var(--iio-success); }
.pd-msg .re{ margin-left:auto; flex-shrink:0; }
/* Im Dialog traegt der Rumpf schon den Abstand. */
.iio-modal-body .pd-msg{ margin-bottom:0; }

/* Datei-Zeile (Binaer Parameter) */
.pd-file{ border:1px solid var(--iio-border-subtle); border-radius:var(--iio-r-md);
  background:var(--iio-bg-1); overflow:hidden; margin-bottom:9px; }
.pd-file-head{ display:grid; grid-template-columns:16px minmax(0,1fr) auto auto; gap:11px;
  align-items:center; width:100%; padding:11px 13px; background:none; border:none; cursor:pointer;
  font-family:inherit; text-align:left; }
.pd-file-head:hover{ background:var(--iio-bg-3); }
.pd-file-head:focus-visible{ outline:2px solid var(--iio-info); outline-offset:-2px; }
.pd-file-head .chev{ width:13px; height:13px; stroke:var(--iio-text-3); fill:none; stroke-width:2.2;
  stroke-linecap:round; stroke-linejoin:round; transition:transform .22s ease; }
.pd-file.offen .pd-file-head .chev{ transform:rotate(90deg); }
.pd-file-head .id{ font-family:var(--iio-font-mono); font-size:12.5px; font-weight:600;
  color:var(--iio-text-1); overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.pd-file-head .typ{ font-size:11px; color:var(--iio-text-3); font-variant-numeric:tabular-nums;
  white-space:nowrap; }

/* Werkzeugleiste des offenen Kastens */
.pd-ed-tb{ display:flex; align-items:center; gap:9px; padding:9px 13px; flex-wrap:wrap;
  border-top:1px solid var(--iio-border-subtle);
  background:color-mix(in srgb, var(--iio-bg-0) 45%, var(--iio-bg-1)); }
.pd-ed-tb .meta{ font-size:11.5px; color:var(--iio-text-3); font-variant-numeric:tabular-nums; }
.pd-ed-tb .frg{ font-size:12px; color:var(--iio-text-1); flex:1 1 260px; min-width:0; line-height:1.5; }
.pd-ed-tb .frg em{ display:block; font-style:normal; color:var(--iio-text-3); font-size:11.5px;
  margin-top:2px; }
.pd-ed-tb .sp{ margin-left:auto; }

/* Lesezustand */
.pd-ed-read{ margin:0; padding:12px 14px; max-height:420px; overflow:auto;
  background:var(--iio-bg-2); border-top:1px solid var(--iio-border-subtle);
  font-family:var(--iio-font-mono); font-size:11.5px; line-height:1.65; color:var(--iio-text-1);
  white-space:pre; tab-size:2; overflow-wrap:normal; }

/* Schreibzustand: Rinne + Feld als eine Flaeche.
   Die Hoehe haengt am Rahmen, NICHT am Inhalt: die Rinne traegt eine Ziffer je Zeile, bei einer
   langen Datei also hunderte. Als Grid-Kind wuerde sie die Zeile auf ihre volle Laenge aufziehen,
   das Feld daneben mitstrecken - und weil das Feld zusaetzlich selbst scrollt, lagen zwei Scroller
   uebereinander: Ziffern und Text liefen auseinander, unterhalb des Textes blieb die gestreckte
   Flaeche leer. Deshalb: fester Rahmen, beide Kinder duerfen schrumpfen (min-height:0), und
   gescrollt wird nur im Feld - die Rinne zieht per scrollTop nach. */
.pd-ed-wrap{ display:grid; grid-template-columns:auto minmax(0,1fr); align-items:stretch;
  height:460px; overflow:hidden; resize:vertical;
  border-top:1px solid var(--iio-border-subtle); background:var(--iio-bg-2); }
.pd-ed-gutter{ padding:12px 8px 12px 12px; text-align:right; user-select:none;
  overflow:hidden; min-height:0;
  border-right:1px solid var(--iio-border-subtle);
  background:color-mix(in srgb, var(--iio-bg-0) 45%, var(--iio-bg-2));
  font-family:var(--iio-font-mono); font-size:11.5px; line-height:19px;
  color:var(--iio-text-4); font-variant-numeric:tabular-nums; }
.pd-ed-gutter .fehl{ color:var(--iio-critical); font-weight:700; }
/* Ganzzahlige Zeilenhoehe an beiden Stellen: bei 1.65 auf 11,5px rundet der Browser je Zeile,
   und Ziffer und Textzeile driften ueber die Laenge einer Datei sichtbar auseinander. */
.pd-ed-area{ display:block; width:100%; height:100%; min-height:0; resize:none;
  overflow:auto; padding:12px 14px;
  border:none; outline:none; background:transparent; color:var(--iio-text-1);
  font-family:var(--iio-font-mono); font-size:11.5px; line-height:19px; tab-size:2;
  white-space:pre; overflow-wrap:normal; box-sizing:border-box; }
.pd-ed-area:focus{ box-shadow:inset 3px 0 0 var(--iio-brand-fg); }

/* Statuszeile: Balken plus Text, kein roter Kasten */
.pd-ed-status{ display:flex; align-items:center; gap:10px; padding:9px 13px;
  border-top:1px solid var(--iio-border-subtle); font-size:12px; color:var(--iio-text-2);
  background:var(--iio-bg-1); }
.pd-ed-status .bar{ width:2px; height:14px; flex-shrink:0; background:var(--iio-text-4); }
.pd-ed-status.ok .bar{ background:var(--iio-success); }
.pd-ed-status.warn .bar{ background:var(--iio-warning); }
.pd-ed-status.bad .bar{ background:var(--iio-critical); }
.pd-ed-status.bad{ color:var(--iio-text-1); }
.pd-ed-status .stelle{ font-family:var(--iio-font-mono); font-weight:600; color:var(--iio-critical);
  font-variant-numeric:tabular-nums; }
.pd-ed-status .zaehler{ margin-left:auto; color:var(--iio-text-3); font-variant-numeric:tabular-nums;
  white-space:nowrap; }
.pd-ed-status .zaehler .neu{ color:var(--iio-success); } .pd-ed-status .zaehler .weg{ color:var(--iio-critical); }

/* Rueckfallkopie nach dem Speichern */
.pd-ed-zurueck{ display:flex; align-items:center; gap:9px; padding:8px 13px;
  border-top:1px dashed var(--iio-border-subtle); font-size:11.5px; color:var(--iio-text-3); }
.pd-ed-zurueck button{ background:none; border:none; padding:2px; font-family:inherit; font-size:11.5px;
  color:var(--iio-text-2); text-decoration:underline; text-underline-offset:3px; cursor:pointer; }
.pd-ed-zurueck button:hover{ color:var(--iio-text-1); }

/* Vergleich im Bestaetigungsdialog */
.pd-diff{ max-height:330px; overflow:auto; padding:10px 12px; background:var(--iio-bg-1);
  border:1px solid var(--iio-border-subtle); border-radius:var(--iio-r-sm);
  font-family:var(--iio-font-mono); font-size:11.5px; line-height:1.65; white-space:pre;
  overflow-wrap:normal; }
.pd-diff .zl{ display:grid; grid-template-columns:46px 12px minmax(0,1fr); gap:8px; }
.pd-diff .nr{ color:var(--iio-text-4); text-align:right; font-variant-numeric:tabular-nums;
  user-select:none; }
.pd-diff .zl.gleich{ color:var(--iio-text-3); }
.pd-diff .zl.weg{ color:var(--iio-critical); background:var(--iio-diff-del-bg); }
.pd-diff .zl.neu{ color:var(--iio-success); background:var(--iio-diff-add-bg); }
.pd-diff .zl .sig{ font-weight:700; }
.pd-diff-mehr{ padding:7px 12px; font-size:11px; color:var(--iio-text-3); }
.pd-diff-legende{ display:flex; gap:16px; margin-bottom:7px; font-size:10px; letter-spacing:.05em;
  text-transform:uppercase; color:var(--iio-text-3); }
.pd-diff-legende i{ display:inline-block; width:8px; height:8px; border-radius:2px; margin-right:5px; }
.pd-diff-legende i.weg{ background:var(--iio-critical); } .pd-diff-legende i.neu{ background:var(--iio-success); }

/* Der eine Satz zur Folge. Auf PROD wird er zur Kante. */
.pd-folge{ display:flex; gap:11px; align-items:flex-start; padding:11px 13px;
  border:1px solid var(--iio-border); border-left:2px solid var(--iio-text-4);
  border-radius:var(--iio-r-sm); background:var(--iio-bg-1);
  font-size:12.5px; line-height:1.55; color:var(--iio-text-2); }
.pd-folge.prod{ border-left-color:var(--iio-signal);
  background:color-mix(in srgb, var(--iio-signal) 5%, var(--iio-bg-1)); }
.pd-folge b{ color:var(--iio-text-1); font-weight:600; }
.pd-folge svg{ width:16px; height:16px; flex-shrink:0; margin-top:1px; fill:none;
  stroke:var(--iio-text-3); stroke-width:1.9; stroke-linecap:round; stroke-linejoin:round; }
.pd-folge.prod svg{ stroke:var(--iio-signal); }

/* Anlege-Dialoge */
.pd-modal-mono{ font-family:var(--iio-font-mono); letter-spacing:.01em; }
.pd-modal-xml{ min-height:210px; font-family:var(--iio-font-mono); font-size:11.5px;
  line-height:1.65; tab-size:2; white-space:pre; overflow-wrap:normal; }
.pd-modal-fehler{ font-size:12px; color:var(--iio-critical); line-height:1.5; }

/* Ohne Zeigegeraet gibt es kein Hover - dort waeren Bearbeiten und Löschen sonst
   unauffindbar. Betrifft genau die Breiten, in denen die Seite einspaltig umbricht. */
@media(hover:none){
  .pd-tr .acts .pd-ic{ opacity:1; }
}

@media(prefers-reduced-motion:reduce){
  .pd-root *{ animation:none!important; transition:none!important; }
  .pd-tr .acts .pd-ic{ opacity:1; }
}

/* ══ Kopieren: Auswahlmodus in der Liste ════════════════════════════════════ */

/* Begruendungszeile neben einem gesperrten Knopf. Ausgrauen ohne Grund ist der Blocker,
   der Grund steht deshalb daneben und nicht im title. */
.pd-ctx-grund{ font-size:11.5px; color:var(--iio-text-3); padding-bottom:11px;
  max-width:260px; line-height:1.4; }

/* Zweite Kopfzeile der Liste, nur im Auswahlmodus. */
.pd-selbar{ display:flex; align-items:center; gap:10px; padding:8px 13px;
  border-bottom:1px solid var(--iio-border-subtle);
  background:color-mix(in srgb, var(--iio-bg-0) 55%, var(--pd-raise)); }
.pd-selbar button.alle{ display:inline-flex; align-items:center; gap:8px; padding:2px 4px;
  background:none; border:none; font-family:inherit; font-size:12px; color:var(--iio-text-2);
  cursor:pointer; }
.pd-selbar button.alle:hover{ color:var(--iio-text-1); }
.pd-selbar button.alle:focus-visible{ outline:2px solid var(--iio-info); outline-offset:2px; }
.pd-selbar .versteckt{ margin-left:auto; font-size:11px; color:var(--iio-text-3);
  text-align:right; line-height:1.35; }

/* Hinweis unter dem Suchfeld, nur wenn er zutrifft. */
.pd-selnote{ padding:7px 13px; border-bottom:1px solid var(--iio-border-subtle);
  font-size:11px; color:var(--iio-text-3); line-height:1.45; }

/* Das gezeichnete Kaestchen. Fuellung --iio-brand-fg, Haken in --iio-bg-2:
   dieses Paar traegt in beiden Themen (hell dunkel auf weiss, dunkel hell auf dunkel). */
.pd-kbox{ width:15px; height:15px; flex-shrink:0; border:1.5px solid var(--iio-text-4);
  border-radius:3px; background:var(--iio-bg-1); position:relative;
  transition:background var(--iio-tx-fast,120ms ease), border-color var(--iio-tx-fast,120ms ease); }
.pd-kbox.an{ background:var(--iio-brand-fg); border-color:var(--iio-brand-fg); }
.pd-kbox.an::after{ content:""; position:absolute; left:4px; top:1px; width:4px; height:8px;
  border:solid var(--iio-bg-2); border-width:0 2px 2px 0; transform:rotate(42deg); }
.pd-kbox.teil{ background:var(--iio-brand-fg); border-color:var(--iio-brand-fg); }
.pd-kbox.teil::after{ content:""; position:absolute; left:2px; top:6px; width:9px; height:0;
  border-top:2px solid var(--iio-bg-2); }

/* Zeilen im Auswahlmodus: zweispaltig statt block. */
.pd-rows.waehlend .pd-row{ display:grid; grid-template-columns:15px minmax(0,1fr);
  gap:10px; align-items:center; }
.pd-rows.waehlend .pd-row .txt{ min-width:0; }
/* Nicht kopierbare Kennungen tragen den Grund rechts - dafuer eine dritte Spalte. */
.pd-rows.waehlend .pd-row.nichtkopierbar{ grid-template-columns:15px minmax(0,1fr) auto; }
/* Auswahl und "wird rechts gezeigt" sind zwei verschiedene Tatsachen und bleiben
   unterscheidbar: Auswahl = Kaestchen plus Teal-Tönung, gezeigt = Persimmon-Kante links. */
.pd-rows.waehlend .pd-row.gewaehlt{
  background:color-mix(in srgb, var(--iio-brand-fg) 8%, transparent); }
.pd-rows.waehlend .pd-row.gewaehlt:hover{
  background:color-mix(in srgb, var(--iio-brand-fg) 13%, transparent); }
.pd-row.nichtkopierbar{ cursor:not-allowed; }
.pd-row.nichtkopierbar .pid, .pd-row.nichtkopierbar .inh{ color:var(--iio-text-4); }
.pd-row .nk{ font-size:10.5px; color:var(--iio-text-3); white-space:nowrap; }

/* Ruhende Detailspalte: eine Zeile sagt warum, der Inhalt bleibt lesbar. */
.pd-ruhe{ display:flex; align-items:center; gap:9px; padding:7px 18px;
  border-bottom:1px solid var(--iio-border-subtle);
  background:color-mix(in srgb, var(--iio-brand-fg) 6%, var(--pd-raise));
  font-size:11.5px; color:var(--iio-text-2); }
.pd-ruhe .bar{ width:2px; height:13px; background:var(--iio-brand-fg); flex-shrink:0; }

/* ══ Popup: gesperrte Ziele ═════════════════════════════════════════════════ */

/* Kein opacity auf der Zeile - das wuerde den Grund mit verblassen lassen. */
.pd-iw-item.gesperrt{ cursor:not-allowed; }
.pd-iw-item.gesperrt:hover{ background:none; }
.pd-iw-item.gesperrt .nm{ color:var(--iio-text-3); }
.pd-iw-item.gesperrt .st{ color:var(--iio-text-4); }
.pd-iw-item.gesperrt .st.prod{ color:var(--iio-text-4); font-weight:400; }
.pd-iw-item .schloss{ width:12px; height:12px; flex-shrink:0; fill:none; stroke:var(--iio-text-4);
  stroke-width:2; stroke-linecap:round; stroke-linejoin:round; }
/* Der kurze Grund steht in der Zeile, rechts, in voller Deckkraft. */
.pd-iw-item .grund{ font-size:11px; color:var(--iio-text-3); flex-shrink:0; white-space:nowrap; }
/* Der ganze Satz steht einmal unter der Space-Ueberschrift. */
.pd-iw-grund{ padding:6px 10px 8px; font-size:11.5px; color:var(--iio-text-3); line-height:1.5; }
.pd-iw-grund b{ color:var(--iio-text-2); font-weight:600; }

/* ══ Kopier-Dialog ══════════════════════════════════════════════════════════ */
.iio-modal.pd-kop{ width:620px; max-width:94vw; }
.iio-modal.pd-kop .iio-modal-body{ display:flex; flex-direction:column; gap:14px; }

.pd-kop-bilanz{ font-size:13px; color:var(--iio-text-1); line-height:1.55; }
.pd-kop-bilanz b{ font-weight:600; font-variant-numeric:tabular-nums; }
.pd-kop-bilanz .neben{ display:block; margin-top:3px; font-size:12px; color:var(--iio-text-3); }

.pd-kop-liste{ border:1px solid var(--iio-border-subtle); border-radius:var(--iio-r-md);
  background:var(--iio-bg-1); overflow:hidden; }
.pd-kop-zeile{ border-top:1px solid var(--iio-border-subtle); }
.pd-kop-zeile:first-child{ border-top:none; }
.pd-kop-kopf{ display:grid; grid-template-columns:2px minmax(0,1fr) auto 14px; gap:11px;
  align-items:center; width:100%; padding:10px 13px; background:none; border:none;
  font-family:inherit; text-align:left; }
button.pd-kop-kopf{ cursor:pointer; }
button.pd-kop-kopf:hover{ background:var(--iio-bg-3); }
button.pd-kop-kopf:focus-visible{ outline:2px solid var(--iio-info); outline-offset:-2px; }
.pd-kop-kopf .bar{ width:2px; height:26px; background:var(--iio-text-4); }
.pd-kop-kopf .bar.neu{ background:var(--iio-success); }
.pd-kop-kopf .bar.ueber{ background:var(--iio-warning); }
.pd-kop-kopf .bar.fehl{ background:var(--iio-critical); }
.pd-kop-kopf .pid{ font-family:var(--iio-font-mono); font-size:12.5px; font-weight:600;
  color:var(--iio-text-1); overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.pd-kop-kopf .folge{ font-size:11.5px; color:var(--iio-text-3); font-variant-numeric:tabular-nums;
  white-space:nowrap; }
.pd-kop-kopf .folge .warn{ color:var(--iio-warning); }
.pd-kop-kopf .folge .bad{ color:var(--iio-critical); }
.pd-kop-kopf .chev{ width:12px; height:12px; stroke:var(--iio-text-4); fill:none; stroke-width:2.2;
  stroke-linecap:round; stroke-linejoin:round; transition:transform .22s ease; }
.pd-kop-zeile.offen .chev{ transform:rotate(90deg); }

.pd-kop-werte{ padding:4px 13px 12px 26px; background:var(--iio-bg-2);
  border-top:1px solid var(--iio-border-subtle); }
.pd-kop-wkopf{ display:grid; grid-template-columns:minmax(90px,160px) minmax(0,1fr); gap:12px;
  padding:7px 0 5px; font-size:10px; font-weight:600; letter-spacing:.1em; text-transform:uppercase;
  color:var(--iio-text-3); }
.pd-kop-wert{ display:grid; grid-template-columns:minmax(90px,160px) minmax(0,1fr); gap:12px;
  padding:6px 0; border-top:1px solid var(--iio-border-subtle); font-size:11.5px; align-items:start; }
.pd-kop-wert .k{ font-family:var(--iio-font-mono); color:var(--iio-text-2);
  overflow-wrap:anywhere; }
.pd-kop-wert .paar{ display:flex; align-items:baseline; gap:8px; flex-wrap:wrap;
  font-family:var(--iio-font-mono); overflow-wrap:anywhere; }
.pd-kop-wert .alt{ color:var(--iio-critical); text-decoration:line-through;
  text-decoration-color:color-mix(in srgb, var(--iio-critical) 45%, transparent); }
.pd-kop-wert .pfeil{ color:var(--iio-text-4); }
.pd-kop-wert .neu{ color:var(--iio-success); }
.pd-kop-wert .hinw{ grid-column:1 / -1; font-size:11px; color:var(--iio-text-3); line-height:1.45; }
/* Der Hinweis zur Datei gehoert neben ihren Namen, nicht darunter. */
.pd-kop-wert .hinw.sp2{ grid-column:2; }
.pd-kop-fehler{ padding:2px 0 0; font-family:var(--iio-font-mono); font-size:11px;
  color:var(--iio-text-2); line-height:1.6; overflow-wrap:anywhere; }

/* Platzhalter waehrend die Vorschau laedt - dieselbe Ruhe-Animation wie die Liste. */
.pd-kop-skel{ height:40px; border-top:1px solid var(--iio-border-subtle);
  background:var(--iio-bg-3); animation:pdShim 1.2s ease-in-out infinite; }
.pd-kop-skel:first-child{ border-top:none; }

/* Laufender Vorgang: eine ruhige Linie, keine erfundene Prozentzahl. */
.pd-kop-lauf{ display:flex; flex-direction:column; gap:9px; padding:6px 0; }
.pd-kop-lauf .txt{ font-size:12.5px; color:var(--iio-text-2); }
.pd-kop-lauf .schiene{ height:2px; background:var(--iio-border-subtle); overflow:hidden; }
.pd-kop-lauf .schiene i{ display:block; height:100%; width:38%; background:var(--iio-signal);
  animation:pdLauf 1.35s ease-in-out infinite; }
@keyframes pdLauf{ 0%{ transform:translateX(-100%); } 100%{ transform:translateX(300%); } }

.pd-kop-fuss-link{ background:none; border:none; padding:2px; font-family:inherit; font-size:12px;
  color:var(--iio-text-2); text-decoration:underline; text-underline-offset:3px; cursor:pointer; }
.pd-kop-fuss-link:hover{ color:var(--iio-text-1); }

/* ── Zielwerte anpassen ─────────────────────────────────────────────────────── */
/* Die Werkzeugspalte kommt nur dort dazu, wo sich etwas anpassen laesst - die Ueberschrift
   traegt sie mit, sonst steht sie nicht mehr ueber ihrer Spalte. */
.pd-kop-wert.werkzeug,
.pd-kop-werte .pd-kop-wkopf{ grid-template-columns:minmax(90px,160px) minmax(0,1fr) auto; }
.pd-kop-wert .werkz{ display:flex; align-items:center; gap:2px; }
.pd-kop-wert .edit{ flex:1 1 160px; min-width:0; height:28px; padding:0 8px;
  border:1px solid var(--iio-brand-fg); border-radius:var(--iio-r-sm); background:var(--iio-bg-1);
  color:var(--iio-text-1); font-family:var(--iio-font-mono); font-size:11.5px; outline:none;
  box-sizing:border-box; }
.pd-kop-wert .edit:focus{ box-shadow:0 0 0 3px rgba(10,61,74,.12); }
[data-theme="dark"] .pd-root .pd-kop-wert .edit:focus{ box-shadow:0 0 0 3px rgba(90,154,224,.18); }
/* Angepasstes traegt dieselbe Farbe wie das Auswahlkaestchen: beides ist eine Absicht des
   Nutzers, die es im Tenant noch nicht gibt. Im laufenden Satz nur die Farbe, keine Groesse. */
.pd-kop-wert .angepasst{ font-size:10.5px; color:var(--iio-brand-fg); white-space:nowrap; }
.pd-kop-wert .angepasst-satz{ color:var(--iio-brand-fg); font-weight:600; }
.pd-kop-kopf .folge .ang{ color:var(--iio-brand-fg); }
/* Frage vor dem Verwerfen: sie steht im Fuss, wo der Klick herkam. */
.pd-kop-verwerfen{ font-size:12.5px; color:var(--iio-text-1); line-height:1.45; }

/* Der Editor ersetzt den Dialoginhalt, statt ihn zu sprengen: eine andere Frage, dieselbe
   Flaeche. Deshalb darf das Fenster dabei breiter werden. */
.iio-modal.pd-kop.mit-editor{ width:900px; }
.pd-kop-edkopf{ display:flex; align-items:baseline; gap:8px; flex-wrap:wrap;
  font-size:12.5px; color:var(--iio-text-3); }
.pd-kop-edkopf .id{ font-family:var(--iio-font-mono); font-size:13px; font-weight:600;
  color:var(--iio-text-1); }
.pd-kop-ed{ border:1px solid var(--iio-border-subtle); border-radius:var(--iio-r-md);
  overflow:hidden; }
.pd-kop-ed .pd-ed-wrap{ border-top:none; height:330px; }

@media(prefers-reduced-motion:reduce){
  .pd-kop-lauf .schiene i{ animation:none; width:100%; opacity:.55; }
}
`;

/* ── Helfer ────────────────────────────────────────────────────────────────── */

// Base64 <-> Text mit UTF-8-Treue. atob/btoa allein zerlegen UTF-8 in Latin-1: bei
// ContentType "xsl;encoding=UTF-8" zerschiesst schon Oeffnen-und-Speichern jeden Umlaut.
// Blockweise, weil ein Spread ueber 1,5 MB den Aufrufstapel sprengt.
const b64ZuText = (b64) => {
  const bin = atob(b64 || "");
  const bytes = Uint8Array.from(bin, c => c.charCodeAt(0));
  return new TextDecoder("utf-8").decode(bytes);
};
const textZuB64 = (text) => {
  const bytes = new TextEncoder().encode(text || "");
  let bin = "";
  for (let i = 0; i < bytes.length; i += 8192)
    bin += String.fromCharCode.apply(null, bytes.subarray(i, i + 8192));
  return btoa(bin);
};

// Zahlen und Uhrzeiten folgen der gewaehlten Sprache. "1,5 KB" liest ein englischsprachiger
// Nutzer als eintausendfuenfhundert - ein festes Gebietsschema verfaelscht damit die Zahl
// selbst. Modulweit ueber die i18next-Instanz statt ueber den Hook: kb() und uhrzeit() stehen
// ausserhalb jeder Komponente.
const gebiet = () => (i18n.language === "en" ? "en-GB" : "de-DE");

const kb = (bytes) => bytes < 1024
  ? `${bytes} B`
  : `${(bytes / 1024).toLocaleString(gebiet(), {minimumFractionDigits:1, maximumFractionDigits:1})} KB`;

const uhrzeit = () => new Date().toLocaleTimeString(gebiet(), {hour:"2-digit", minute:"2-digit"});

// Feldgrenzen aus dem $metadata des Tenants.
const GRENZE = { pid:60, paramId:1500, paramWert:4000, typ:300, datei:1572864, agentur:120, schema:120, kennung:255 };
const PD_NS_SYSTEM = "http://sap.com/xi/XI/System";

// Wohlgeformtheit ohne Bibliothek. Firefox und Chromium melden unterschiedlich -
// deshalb wird nur der Text des parsererror-Knotens ausgewertet.
// Zweite Stufe: das Wurzelelement. Die API nimmt eine fachlich falsche Datei kommentarlos
// an, die Pipeline faellt erst zur Laufzeit um - deshalb wird es hier benannt. Bewusst
// Hinweis und kein Riegel: ein Binaerparameter muss keine Empfaengerermittlung sein.
//
// Die Funktion steht ausserhalb jeder Komponente und hat deshalb kein t(). Sie liefert
// SCHLUESSEL in `meldung` und `hinweis`, die Anzeigestelle uebersetzt sie. Ausnahme ist der
// Text des Browsers selbst: der kommt fertig und in dessen Sprache, `roh` markiert ihn.
function pruefeXml(text) {
  if (!text || !text.trim()) return { ok:false, meldung:"pruefung.leer" };
  const doc = new DOMParser().parseFromString(text, "application/xml");
  const err = doc.querySelector("parsererror");
  if (err) {
    const roh = err.textContent || "";
    const z = roh.match(/[Ll]ine[: ]+(\d+)/) || roh.match(/Zeile[: ]+(\d+)/);
    const s = roh.match(/[Cc]olumn[: ]+(\d+)/) || roh.match(/Spalte[: ]+(\d+)/);
    const grund = (roh.split("\n").find(l => l.trim() && !/^This page/.test(l)) || "").trim();
    return { ok:false, zeile: z ? +z[1] : null, spalte: s ? +s[1] : null,
      meldung: grund || "pruefung.nichtWohlgeformt", roh: !!grund };
  }
  const wurzel = doc.documentElement;
  const lokal = wurzel?.localName;
  // Die Datei ist ein Stylesheet: <Receivers> entsteht erst in der AUSGABE, typischerweise
  // in <xsl:template match="/">. Deshalb wird nur gesucht, ob es ueberhaupt vorkommt.
  if (lokal === "stylesheet" || lokal === "transform") {
    const erzeugtEmpfaenger = doc.getElementsByTagNameNS(PD_NS_SYSTEM, "Receivers").length > 0;
    return erzeugtEmpfaenger ? { ok:true }
      : { ok:true, hinweis:"pruefung.keinReceivers", werte:{ namensraum: PD_NS_SYSTEM } };
  }
  // Oder die Datei ist bereits die fertige Empfaengerliste.
  if (lokal === "Receivers" && wurzel.namespaceURI === PD_NS_SYSTEM) return { ok:true };
  // Der Platzhalter heisst namensraum und NICHT ns: "ns" ist bei i18next die Angabe des
  // Namensraums im Katalog. Als Wert uebergeben schlaegt die Suche fehl und der Schluessel
  // selbst stuende auf dem Schirm.
  return { ok:true, hinweis:"pruefung.fremdeWurzel",
    werte:{ wurzel: wurzel?.nodeName || "?", namensraum: PD_NS_SYSTEM } };
}

// Beide Stellen, an denen eine Pruefung angezeigt wird, sagen dasselbe: Browsertext woertlich,
// eigener Text uebersetzt.
const pruefMeldung = (p, t) => (p.roh ? p.meldung : t(p.meldung));
const pruefStelle = (p, t) => (p.spalte
  ? t("editor.zeileSpalte", { zeile: p.zeile, spalte: p.spalte })
  : t("editor.zeile", { zeile: p.zeile }));

// Dialog-Tasten nach Hausvorbild (DokuPage): Escape schliesst, der Fokus bleibt im Dialog.
function useDialogTasten(offen, kastenRef, aufSchliessen) {
  useEffect(() => {
    if (!offen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") { aufSchliessen(); return; }
      if (e.key !== "Tab") return;
      const el = kastenRef.current; if (!el) return;
      const f = [...el.querySelectorAll('button,[href],input,select,textarea,[tabindex]:not([tabindex="-1"])')]
        .filter(x => !x.disabled && x.offsetParent !== null);
      if (!f.length) return;
      const erste = f[0], letzte = f[f.length - 1];
      if (e.shiftKey && document.activeElement === erste) { e.preventDefault(); letzte.focus(); }
      else if (!e.shiftKey && document.activeElement === letzte) { e.preventDefault(); erste.focus(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [offen, kastenRef, aufSchliessen]);
}

// Zeilenweiser Vergleich (LCS). Ab 4000 Zeilen zusammen wird nicht mehr verglichen -
// dann nennt der Dialog nur die Zeilenbilanz. Die Matrix waere sonst zu gross.
function zeilenVergleich(alt, neu) {
  const a = (alt || "").split("\n"), b = (neu || "").split("\n");
  const m = a.length, n = b.length;
  if (m + n > 4000) return null;
  const lcs = Array.from({length:m+1}, () => new Uint32Array(n+1));
  for (let i = m-1; i >= 0; i--) for (let j = n-1; j >= 0; j--)
    lcs[i][j] = a[i] === b[j] ? lcs[i+1][j+1] + 1 : Math.max(lcs[i+1][j], lcs[i][j+1]);
  const out = []; let i = 0, j = 0;
  while (i < m && j < n) {
    if (a[i] === b[j]) { out.push({art:"gleich", zeile:j+1, text:b[j]}); i++; j++; }
    else if (lcs[i+1][j] >= lcs[i][j+1]) { out.push({art:"weg", zeile:i+1, text:a[i]}); i++; }
    else { out.push({art:"neu", zeile:j+1, text:b[j]}); j++; }
  }
  while (i < m) { out.push({art:"weg", zeile:i+1, text:a[i]}); i++; }
  while (j < n) { out.push({art:"neu", zeile:j+1, text:b[j]}); j++; }
  return out;
}

// Ersetzte Zeilen zaehlen als "geaendert", der Ueberhang als hinzugefuegt bzw. entfernt.
function zeilenBilanz(bloecke, alt, neu) {
  if (!bloecke) {
    const a = (alt || "").split("\n").length, b = (neu || "").split("\n").length;
    return { geaendert:0, neu:Math.max(0, b-a), weg:Math.max(0, a-b), grob:true };
  }
  const nNeu = bloecke.filter(z => z.art === "neu").length;
  const nWeg = bloecke.filter(z => z.art === "weg").length;
  const paar = Math.min(nNeu, nWeg);
  return { geaendert:paar, neu:nNeu - paar, weg:nWeg - paar, grob:false };
}

// Nur geaenderte Zeilen mit je zwei Zeilen Umfeld, hoechstens 400.
function sichtbarerVergleich(bloecke, max = 400) {
  if (!bloecke) return { zeilen:null, mehr:0 };
  const wichtig = new Set();
  bloecke.forEach((z, i) => {
    if (z.art === "gleich") return;
    for (let k = i - 2; k <= i + 2; k++) if (k >= 0 && k < bloecke.length) wichtig.add(k);
  });
  const idx = [...wichtig].sort((a, b) => a - b);
  return { zeilen: idx.slice(0, max).map(i => bloecke[i]), mehr: Math.max(0, idx.length - max) };
}

const IcoPlus  = () => <svg viewBox="0 0 24 24"><path d="M12 5v14M5 12h14"/></svg>;
const IcoStift = () => <svg viewBox="0 0 24 24"><path d="M12 20h9"/><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>;
const IcoMuell = () => <svg viewBox="0 0 24 24"><path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6"/></svg>;
const IcoX     = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>;
const IcoKopie = () => <svg viewBox="0 0 24 24"><rect x="9" y="9" width="12" height="12" rx="2"/><path d="M5 15H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v1"/></svg>;
const IcoSchloss = () => <svg className="schloss" viewBox="0 0 24 24" aria-hidden="true"><rect x="4" y="11" width="16" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></svg>;
const IcoZurueck = () => <svg viewBox="0 0 24 24"><path d="M3 8a9 9 0 1 1-1 6"/><path d="M3 3v5h5"/></svg>;

/* ── XSLT-Editor: Textfeld mit Rinne, dazu die Statuszeile ─────────────────── */
// EIN Baustein fuer beide Orte, an denen ein XSLT bearbeitet wird: die Detailspalte (Quelle
// pflegen) und der Kopierdialog (Zielfassung anpassen). Zwei Textfelder mit eigener Rinne
// waeren zwei Wege zu derselben Handlung - und einer davon waere irgendwann der schlechtere.

const PdXmlFeld = ({ wert, setzeWert, fehlerZeile, label, readOnly }) => {
  const rinneRef = useRef(null);
  const tabAlsEinrueckung = (e) => {
    // Escape verlaesst das Feld und NUR das Feld: sonst raeumt dieselbe Taste zugleich den
    // Dialog darum herum ab und der getippte Stand ist weg.
    if (e.key === "Escape") { e.stopPropagation(); e.currentTarget.blur(); return; }
    if (e.key !== "Tab" || e.shiftKey || readOnly) return;
    e.preventDefault();
    const el = e.currentTarget;
    const s = el.selectionStart, t = el.selectionEnd;
    setzeWert(el.value.slice(0, s) + "  " + el.value.slice(t));
    requestAnimationFrame(() => { el.selectionStart = el.selectionEnd = s + 2; });
  };
  return (
    <div className="pd-ed-wrap">
      <div className="pd-ed-gutter" ref={rinneRef} aria-hidden="true">
        {wert.split("\n").map((_, i) => (
          <div key={i} className={fehlerZeile === i + 1 ? "fehl" : undefined}>{i + 1}</div>
        ))}
      </div>
      <textarea className="pd-ed-area" value={wert} spellCheck={false} wrap="off"
        readOnly={readOnly} aria-label={label}
        onChange={e => setzeWert(e.target.value)}
        onScroll={e => { if (rinneRef.current) rinneRef.current.scrollTop = e.target.scrollTop; }}
        onKeyDown={tabAlsEinrueckung} />
    </div>
  );
};

// zweck steht im Satz, weil derselbe Befund einmal das Speichern und einmal das Kopieren
// verhindert - die Lage ist dieselbe, die Folge nicht. Uebergeben wird die KENNUNG des Zwecks
// ("speichern"/"uebernehmen"), nicht das Wort: die Beugung unterscheidet sich je Sprache, im
// Englischen sogar zwischen Satzanfang ("Saving") und Satzmitte ("saved").
const PdXmlStatus = ({ pruefung, groesse, zweck = "speichern", children }) => {
  const { t } = useTranslation(["partnerdirectory", "gemeinsam"]);
  const zuGross = groesse > GRENZE.datei;
  return (
    <div className={"pd-ed-status " + (zuGross || !pruefung.ok ? "bad" : pruefung.hinweis ? "warn" : "ok")}>
      <span className="bar" />
      {zuGross
        ? <span>{t("editor.zuGross", { groesse: kb(groesse), verb: t(`editor.verbPassiv.${zweck}`) })}</span>
        : !pruefung.ok
          ? <span>{pruefung.zeile
              ? <><span className="stelle">{pruefStelle(pruefung, t)}</span>: {pruefMeldung(pruefung, t)}</>
              : pruefMeldung(pruefung, t)}</span>
          : pruefung.hinweis
            ? <span>{t(pruefung.hinweis, pruefung.werte)} {t("editor.trotzdem", { verb: t(`editor.verbGerund.${zweck}`) })}</span>
            : <span>{t("editor.wohlgeformt")}</span>}
      {children}
    </div>
  );
};

/* ── String-Parameter-Zeile ────────────────────────────────────────────────── */

const PdStringZeile = ({ param, pid, instId, darfSchreiben, letzterInhalt, ruht, aufAenderung, aufFehler }) => {
  const { t } = useTranslation(["partnerdirectory", "gemeinsam"]);
  const [entwurf, setEntwurf] = useState(null);   // null = Lesezustand
  const [frage, setFrage]     = useState(false);
  const [laeuft, setLaeuft]   = useState(false);

  const speichern = async () => {
    setLaeuft(true);
    try {
      // Ueberschreibschutz wie beim XSLT. Der ETag geht mit, wird serverseitig aber nur fuer
      // Benutzer mit experimentellem Caching ausgewertet - deshalb zusaetzlich der gelesene
      // Ist-Wert als Grundlage, der auf jedem Weg geprueft wird.
      await api(`/api/${instId}/partner-directory/string-params/${encodeURIComponent(pid)}/${encodeURIComponent(param.Id)}`,
        { method:"PUT", body: JSON.stringify({ Value: entwurf, etag: param.__metadata?.etag, basisWert: param.Value ?? "" }) });
      setEntwurf(null);
      aufAenderung();
    } catch (e) { aufFehler(e.message); }
    setLaeuft(false);
  };

  const loeschen = async () => {
    setLaeuft(true);
    try {
      await api(`/api/${instId}/partner-directory/string-params/${encodeURIComponent(pid)}/${encodeURIComponent(param.Id)}`,
        { method:"DELETE" });
      setFrage(false);
      aufAenderung();
    } catch (e) { aufFehler(e.message); setLaeuft(false); }
  };

  // Eine offene Rueckfrage traegt ihren Knopf schon - sie muss deshalb selbst ruhen, sonst
  // loescht sie in der Quelle, waehrend der Kopiervorgang deren gespeicherten Stand liest.
  // Zurueckgenommen, nicht verworfen: der Zustand steht nach dem Modus wieder da.
  if (frage) return (
    <div className="pd-tr frage">
      <span className="frg">
        <Trans t={t} i18nKey="param.loeschenFrage" values={{ id: param.Id }} components={{ b: <b/> }} />
        {letzterInhalt && <em>{t("detail.letzterInhalt")}</em>}
        {ruht && <em>{t("detail.loeschenGesperrt")}</em>}
      </span>
      <span className="acts">
        {!ruht && <>
          <button type="button" className="pd-btn ghost sm" onClick={() => setFrage(false)}>{t("gemeinsam:aktion.abbrechen")}</button>
          <button type="button" className="pd-btn danger sm" onClick={loeschen} disabled={laeuft}>
            {laeuft ? t("detail.wirdGeloescht") : t("detail.jaLoeschen")}
          </button>
        </>}
      </span>
    </div>
  );

  return (
    <div className="pd-tr">
      <span className="k" title={param.Id}>{param.Id}</span>
      {entwurf === null
        ? (param.Value
            ? <span className="val">{param.Value}</span>
            : <span className="val leer">{t("wert.leer")}</span>)
        : <input className="edit" value={entwurf} autoFocus={!ruht} maxLength={GRENZE.paramWert}
            readOnly={ruht}
            aria-label={t("param.wertVon", { id: param.Id })}
            onChange={e => setEntwurf(e.target.value)}
            onKeyDown={e => {
              if (ruht) return;
              if (e.key === "Enter") speichern();
              if (e.key === "Escape") setEntwurf(null);
            }} />}
      <span className="acts">
        {entwurf === null ? (darfSchreiben && <>
          <button type="button" className="pd-ic" title={t("gemeinsam:aktion.bearbeiten")}
            aria-label={t("param.ariaBearbeiten", { id: param.Id })}
            onClick={() => setEntwurf(param.Value || "")}><IcoStift/></button>
          <button type="button" className="pd-ic del" title={t("gemeinsam:aktion.loeschen")}
            aria-label={t("param.ariaLoeschen", { id: param.Id })}
            onClick={() => setFrage(true)}><IcoMuell/></button>
        </>) : <>
          {/* Verwerfen ist kein Schreibzugriff und bleibt auch im Auswahlmodus. */}
          <button type="button" className="pd-btn ghost sm" onClick={() => setEntwurf(null)}>{t("detail.verwerfen")}</button>
          {!ruht && (
            <button type="button" className="pd-btn prim sm" onClick={speichern}
              disabled={laeuft || entwurf === (param.Value || "")}>
              {laeuft ? t("detail.wirdGespeichert") : t("gemeinsam:aktion.speichern")}
            </button>
          )}
        </>}
      </span>
    </div>
  );
};

/* ── Datei-Zeile (Binaer Parameter) mit Lese- und Schreibzustand ───────────── */

// Der Bearbeitungsstand (geladener Inhalt, Base64-Grundlage, Entwurf, Rueckfallkopie) liegt
// BEWUSST im Elternzustand: ein Reiterwechsel baut diesen Teilbaum ab, und ein wortlos
// verworfener Entwurf waere genau das Gegenteil dessen, was der Punkt am Reiter verspricht.
const PdDatei = ({ param, pid, instId, darfSchreiben, istProd, letzterInhalt, ruht,
                   stand, setzeStand, aufAenderung, aufFehler }) => {
  const { t } = useTranslation(["partnerdirectory", "gemeinsam"]);
  const inhalt    = stand?.inhalt ?? null;    // geladener Originaltext
  const entwurf   = stand?.entwurf ?? null;   // null = Lesezustand
  const vorherige = stand?.vorherige ?? null; // Rueckfallkopie dieser Sitzung

  // Kommt der Teilbaum mit offenem Entwurf zurueck, muss er ihn sofort zeigen und
  // die abgeleiteten Werte gleich mitbringen - sonst steht der Knopf falsch.
  const [offen, setOffen]         = useState(() => stand?.entwurf != null);
  const [laedt, setLaedt]         = useState(false);
  const [pruefung, setPruefung]   = useState(() => stand?.entwurf != null ? pruefeXml(stand.entwurf) : { ok:true });
  const [bloecke, setBloecke]     = useState(() => stand?.entwurf != null ? zeilenVergleich(stand.inhalt || "", stand.entwurf) : null);
  const [bestaetigen, setBestaetigen] = useState(false);
  const [speichert, setSpeichert] = useState(false);
  const [dialogFehler, setDialogFehler] = useState(null);
  const [konflikt, setKonflikt]   = useState(false);
  const [holt, setHolt]           = useState(false);
  const [standFrisch, setStandFrisch] = useState(false);
  const [frage, setFrage]         = useState(false);
  const [loescht, setLoescht]     = useState(false);
  const dialogRef = useRef(null);

  const setEntwurf = (wert) => setzeStand(s => ({ ...s, entwurf: wert }));

  const text    = entwurf ?? inhalt ?? "";
  const zeilen  = text.split("\n").length;
  const groesse = new TextEncoder().encode(text).length;
  const zuGross = groesse > GRENZE.datei;
  const bilanz  = entwurf == null ? null : zeilenBilanz(bloecke, inhalt, entwurf);

  const dialogZu = useCallback(() => { if (!speichert && !holt) setBestaetigen(false); }, [speichert, holt]);
  useDialogTasten(bestaetigen, dialogRef, dialogZu);

  // Stufe 1 und Bilanz, gemeinsam entprellt - der LCS ist zu teuer fuer jeden Tastendruck.
  useEffect(() => {
    if (entwurf == null) return undefined;
    const t = setTimeout(() => {
      setPruefung(pruefeXml(entwurf));
      setBloecke(zeilenVergleich(inhalt || "", entwurf));
    }, 400);
    return () => clearTimeout(t);
  }, [entwurf, inhalt]);

  // Zuklappen verwirft nichts: der Entwurf bleibt im Zustand, der Punkt am Reiter bleibt stehen.
  const oeffnen = async () => {
    if (offen) { setOffen(false); return; }
    setOffen(true);
    if (inhalt != null) return;
    setLaedt(true);
    try {
      const detail = await api(`/api/${instId}/partner-directory/${encodeURIComponent(pid)}`);
      const bp = (detail.binaryParams || []).find(b => b.Id === param.Id);
      // Der geladene Stand als Base64 ist die Grundlage der Konfliktpruefung beim Speichern:
      // ohne ihn wuerde ein zweiter Bearbeiter am selben XSLT lautlos ueberschrieben.
      setzeStand(s => ({ ...s, inhalt: bp?.Value ? b64ZuText(bp.Value) : "", basis: bp?.Value || "" }));
    } catch (e) { aufFehler(t("datei.ladefehler", { id: param.Id, grund: e.message })); }
    setLaedt(false);
  };

  const kopieren = () => { navigator.clipboard?.writeText(inhalt || ""); };

  const speichern = async () => {
    setSpeichert(true); setDialogFehler(null);
    try {
      const neuB64 = textZuB64(entwurf);
      await api(`/api/${instId}/partner-directory/binary-params/${encodeURIComponent(pid)}/${encodeURIComponent(param.Id)}`,
        { method:"PUT", body: JSON.stringify({ Value: neuB64, ContentType: param.ContentType, basisWert: stand?.basis ?? "" }) });
      setzeStand(s => ({ ...s, inhalt: entwurf, basis: neuB64, entwurf: null,
        vorherige: { text: s?.inhalt ?? "", zeit: uhrzeit() } }));
      setBestaetigen(false); setKonflikt(false); setStandFrisch(false);
      aufAenderung();
    } catch (e) {
      // Der Dialog bleibt offen und traegt die Begruendung selbst: ein Schliessen sieht aus
      // wie Erfolg, und eine Meldung weit oben liest hier niemand. Der Entwurf bleibt stehen -
      // bei einem Konflikt ist die getippte Fassung das Einzige, was der Bearbeiter noch hat.
      setKonflikt(e.status === 409);
      setDialogFehler(e.message);
    }
    setSpeichert(false);
  };

  // Ausgang aus dem Konflikt: fremden Stand zur neuen Grundlage machen, den eigenen Entwurf
  // stehen lassen und den Vergleich gegen den frischen Stand zeigen.
  const holeFremdenStand = async () => {
    setHolt(true); setDialogFehler(null);
    try {
      const detail = await api(`/api/${instId}/partner-directory/${encodeURIComponent(pid)}`);
      const bp = (detail.binaryParams || []).find(b => b.Id === param.Id);
      if (!bp) {
        setDialogFehler(t("datei.wegImTenant"));
        setHolt(false); return;
      }
      const fremd = bp.Value ? b64ZuText(bp.Value) : "";
      setzeStand(s => ({ ...s, inhalt: fremd, basis: bp.Value || "" }));
      setBloecke(zeilenVergleich(fremd, entwurf));
      setKonflikt(false); setStandFrisch(true);
    } catch (e) { setDialogFehler(t("datei.fremdFehler", { grund: e.message })); }
    setHolt(false);
  };

  const loeschen = async () => {
    setLoescht(true);
    try {
      await api(`/api/${instId}/partner-directory/binary-params/${encodeURIComponent(pid)}/${encodeURIComponent(param.Id)}`,
        { method:"DELETE" });
      setFrage(false);
      aufAenderung();
    } catch (e) { aufFehler(e.message); setLoescht(false); }
  };

  const sicht = bestaetigen ? sichtbarerVergleich(bloecke) : { zeilen:null, mehr:0 };

  return (
    <div className={"pd-file" + (offen ? " offen" : "")}>
      <button type="button" className="pd-file-head" onClick={oeffnen} aria-expanded={offen}>
        <svg className="chev" viewBox="0 0 24 24"><polyline points="9 6 15 12 9 18"/></svg>
        <span className="id" title={param.Id}>{param.Id}</span>
        <span className="typ">{param.ContentType}</span>
        <span className="typ">{offen && inhalt != null
          ? t("datei.zeilenGroesse", { count: zeilen, groesse: kb(groesse) }) : ""}</span>
      </button>

      {offen && <>
        {laedt && <div className="pd-ed-tb"><span className="meta">{t("datei.laedt")}</span></div>}

        {!laedt && inhalt != null && <>
          <div className="pd-ed-tb">
            {frage ? <>
              {/* Loeschen ist die zerstoerischere Handlung als Aendern und traegt deshalb
                  dieselbe Folgenbeschreibung und dieselbe PROD-Kennzeichnung. */}
              <span className="frg">
                <Trans t={t} i18nKey="datei.loeschenFrage" values={{ id: param.Id }} components={{ b: <b/> }} />
                {istProd && <em>{t("detail.prodHinweis")}</em>}
                {letzterInhalt && <em>{t("detail.letzterInhalt")}</em>}
                {/* Eine offene Rueckfrage traegt ihren Knopf schon - sie muss deshalb selbst ruhen.
                    Zurueckgenommen, nicht verworfen: nach dem Modus steht sie wieder da. */}
                {ruht && <em>{t("detail.loeschenGesperrt")}</em>}
              </span>
              <span className="sp" />
              {!ruht && <>
                <button type="button" className="pd-btn ghost sm" onClick={() => setFrage(false)}>{t("gemeinsam:aktion.abbrechen")}</button>
                <button type="button" className="pd-btn danger sm" onClick={loeschen} disabled={loescht}>
                  {loescht ? t("detail.wirdGeloescht") : t("detail.jaLoeschen")}
                </button>
              </>}
            </> : <>
              <span className="meta">{t("datei.zeilenGroesse", { count: zeilen, groesse: kb(groesse) })}</span>
              <span className="sp" />
              {entwurf == null ? <>
                {/* Nicht "Kopieren": daneben steht der Kopierknopf, der ins Ziel schreibt. */}
                <button type="button" className="pd-btn ghost sm" onClick={kopieren}>{t("datei.inZwischenablage")}</button>
                {darfSchreiben && <>
                  <button type="button" className="pd-btn ghost sm" onClick={() => { setEntwurf(inhalt); setPruefung(pruefeXml(inhalt)); setBloecke(null); }}>
                    <IcoStift/>{t("gemeinsam:aktion.bearbeiten")}
                  </button>
                  <button type="button" className="pd-btn danger sm" onClick={() => setFrage(true)}>{t("gemeinsam:aktion.loeschen")}</button>
                </>}
              </> : <>
                {/* Der Entwurf bleibt stehen und geht nicht verloren - aber er laesst sich nicht
                    speichern, solange die Auswahl laeuft: sonst schriebe die Quelle in dem
                    Augenblick, in dem der Kopiervorgang ihren gespeicherten Stand liest.
                    Verwerfen ist kein Schreibzugriff und bleibt. */}
                {ruht && <span className="meta">{t("datei.speichernGesperrt")}</span>}
                <button type="button" className="pd-btn ghost sm" onClick={() => { setEntwurf(null); setBloecke(null); }}>{t("detail.verwerfen")}</button>
                {!ruht && (
                  <button type="button" className="pd-btn prim sm" disabled={!pruefung.ok || zuGross || entwurf === inhalt}
                    onClick={() => {
                      setPruefung(pruefeXml(entwurf));
                      setBloecke(zeilenVergleich(inhalt || "", entwurf));
                      setDialogFehler(null); setKonflikt(false); setStandFrisch(false);
                      setBestaetigen(true);
                    }}>
                    {t("datei.aenderungPruefen")}
                  </button>
                )}
              </>}
            </>}
          </div>

          {entwurf == null
            ? <pre className="pd-ed-read">{inhalt}</pre>
            : <>
                <PdXmlFeld wert={entwurf} setzeWert={setEntwurf} fehlerZeile={pruefung.zeile}
                  readOnly={ruht} label={t("datei.ariaBearbeitenInhalt", { id: param.Id })} />
                <PdXmlStatus pruefung={pruefung} groesse={groesse}>
                  {bilanz && (bilanz.geaendert > 0 || bilanz.neu > 0 || bilanz.weg > 0) && (
                    <span className="zaehler">
                      {t("datei.geaendert", { count: bilanz.geaendert })}
                      {bilanz.neu ? <> · <span className="neu">+{bilanz.neu}</span></> : null}
                      {bilanz.weg ? <> · <span className="weg">−{bilanz.weg}</span></> : null}
                    </span>
                  )}
                </PdXmlStatus>
              </>}

          {vorherige && entwurf == null && (
            <div className="pd-ed-zurueck">
              <span>{t("datei.gespeichertUm", { zeit: vorherige.zeit })}</span>
              <button type="button" onClick={() => { setEntwurf(vorherige.text); setPruefung(pruefeXml(vorherige.text)); }}>
                {t("datei.vorherigeEinsetzen")}
              </button>
            </div>
          )}
        </>}
      </>}

      {/* Auch der offene Bestaetigungsdialog ruht: er traegt den Schreibknopf schon.
          Zurueckgenommen, nicht verworfen - nach dem Modus steht er wieder da. */}
      {bestaetigen && bilanz && !ruht && (
        <div className="iio-modal-overlay is-open"
          onMouseDown={e => { if (e.target === e.currentTarget) dialogZu(); }}>
          <div className="iio-modal pd-modal-breit" role="dialog" aria-modal="true" ref={dialogRef}
            aria-labelledby="pd-best-t" onMouseDown={e => e.stopPropagation()}>
            <div className="iio-modal-head">
              <div className="iio-modal-head-left">
                <span className="iio-modal-eyebrow">{pid} · {param.Id}</span>
                <h2 id="pd-best-t">{t("datei.dialogTitel")}</h2>
              </div>
              <button type="button" className="iio-modal-close" onClick={dialogZu}
                disabled={speichert || holt} aria-label={t("gemeinsam:aktion.schliessen")}>
                <IcoX/>
              </button>
            </div>

            <div className="iio-modal-body">
              {dialogFehler && <div className="pd-msg bad"><span>{dialogFehler}</span></div>}
              {standFrisch && (
                <div className="pd-msg ok">
                  <span>{t("datei.standFrisch")}</span>
                </div>
              )}

              <div className={"pd-folge" + (istProd ? " prod" : "")}>
                <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M12 8v5M12 16h.01"/></svg>
                <span>
                  {t("datei.folge")}
                  {istProd && <> <b>{t("detail.prodHinweis")}</b></>}
                </span>
              </div>

              <div>
                <div className="pd-diff-legende">
                  <span><i className="weg" />{t("datei.legendeVorher")}</span>
                  <span><i className="neu" />{t("datei.legendeNachher")}</span>
                </div>
                {sicht.zeilen
                  ? <>
                      <div className="pd-diff">
                        {sicht.zeilen.map((z, i) => (
                          <div key={i} className={"zl " + z.art}>
                            <span className="nr">{z.zeile}</span>
                            <span className="sig">{z.art === "neu" ? "+" : z.art === "weg" ? "−" : " "}</span>
                            <span>{z.text}</span>
                          </div>
                        ))}
                      </div>
                      {sicht.mehr > 0 && <div className="pd-diff-mehr">{t("datei.mehrZeilen", { count: sicht.mehr })}</div>}
                    </>
                  : <div className="pd-diff-mehr">
                      {t("datei.zuGrossVergleich", {
                        vorher: (inhalt || "").split("\n").length, nachher: zeilen })}
                    </div>}
              </div>
            </div>

            <div className="iio-modal-foot">
              <div className="iio-modal-foot-left">
                {!bilanz.grob && (
                  <span style={{fontSize:12, color:"var(--iio-text-3)"}}>
                    {t("datei.fussBilanz", { geaendert: bilanz.geaendert, neu: bilanz.neu, weg: bilanz.weg })}
                  </span>
                )}
              </div>
              <div className="iio-modal-foot-right">
                <button type="button" className="iio-btn iio-btn--secondary" onClick={dialogZu}
                  disabled={speichert || holt}>
                  {t("datei.zurueckBearbeitung")}
                </button>
                {konflikt ? (
                  <button type="button" className="iio-btn iio-btn--signal" onClick={holeFremdenStand} disabled={holt}>
                    {holt ? t("datei.wirdGeholt") : t("datei.fremdenStandHolen")}
                  </button>
                ) : (
                <button type="button" className="iio-btn iio-btn--signal" onClick={speichern} disabled={speichert || holt}>
                  {speichert ? t("datei.wirdUebernommen") : t("datei.uebernehmen")}
                </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ── Alternative-Kennung-Zeile ─────────────────────────────────────────────── */

const PdAltZeile = ({ eintrag, instId, darfSchreiben, letzterInhalt, ruht, aufAenderung, aufFehler }) => {
  const { t } = useTranslation(["partnerdirectory", "gemeinsam"]);
  const [frage, setFrage]   = useState(false);
  const [laeuft, setLaeuft] = useState(false);
  const loeschbar = !!(eintrag.Hexagency && eintrag.Hexscheme && eintrag.Hexid);

  const loeschen = async () => {
    setLaeuft(true);
    try {
      await api(`/api/${instId}/partner-directory/alt-partners/${eintrag.Hexagency}/${eintrag.Hexscheme}/${eintrag.Hexid}`,
        { method:"DELETE" });
      setFrage(false);
      aufAenderung();
    } catch (e) { aufFehler(e.message); setLaeuft(false); }
  };

  if (frage) return (
    <div className="pd-tr frage">
      <span className="frg">
        <Trans t={t} i18nKey="alt.loeschenFrage" values={{ id: eintrag.Id }} components={{ b: <b/> }} />
        {/* Ueber genau diesen Eintrag findet die Laufzeit die Pid. Fehlt er, sieht im Partner
            Directory alles vollstaendig aus und das Szenario wird trotzdem nie gefunden. */}
        {eintrag.Scheme === "SenderInterface" && (
          <em>{t("alt.senderInterfaceWarnung")}</em>
        )}
        {letzterInhalt && <em>{t("detail.letzterInhalt")}</em>}
        {ruht && <em>{t("detail.loeschenGesperrt")}</em>}
      </span>
      <span className="acts">
        {!ruht && <>
          <button type="button" className="pd-btn ghost sm" onClick={() => setFrage(false)}>{t("gemeinsam:aktion.abbrechen")}</button>
          <button type="button" className="pd-btn danger sm" onClick={loeschen} disabled={laeuft}>
            {laeuft ? t("detail.wirdGeloescht") : t("detail.jaLoeschen")}
          </button>
        </>}
      </span>
    </div>
  );

  return (
    <div className="pd-tr cols4">
      <span className="val">{eintrag.Agency}</span>
      <span className="val">{eintrag.Scheme}</span>
      <span className="val">{eintrag.Id}</span>
      <span className="acts">
        {darfSchreiben && (
          <button type="button" className="pd-ic del" onClick={() => setFrage(true)} disabled={!loeschbar}
            title={loeschbar ? t("gemeinsam:aktion.loeschen") : t("alt.nichtLoeschbar")}
            aria-label={t("alt.ariaLoeschen", { id: eintrag.Id })}><IcoMuell/></button>
        )}
      </span>
    </div>
  );
};

/* ── Anlege-Dialog (Kennung, Parameter, Datei, alternative Kennung) ────────── */

const PdAnlegen = ({ art, pid, instId, vorhandenePids, aufFertig, aufAbbruch }) => {
  const { t } = useTranslation(["partnerdirectory", "gemeinsam"]);
  const [f, setF] = useState({
    pid:"", id:"", wert:"", typ:"xsl;encoding=UTF-8", inhalt:"",
    agentur:"", schema:"", kennung:"",
  });
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState(null);
  const setzen = (k, w) => setF(p => ({...p, [k]:w}));

  const pruefungXml = art === "datei" && f.inhalt.trim() ? pruefeXml(f.inhalt) : null;
  const pidDoppelt  = art === "eintrag" && !!vorhandenePids?.includes(f.pid.trim());
  // Der Namensraum SAP_ gehoert dem Trading Partner Management, das ihn selbst verwaltet.
  const pidReserviert = art === "eintrag" && /^SAP_/i.test(f.pid.trim());
  const dateiGroesse  = art === "datei" ? new TextEncoder().encode(f.inhalt).length : 0;
  const dateiZuGross  = dateiGroesse > GRENZE.datei;

  const bereit = art === "eintrag" ? !!(f.pid.trim() && f.id.trim()) && !pidDoppelt && !pidReserviert
    : art === "param"  ? !!f.id.trim()
    : art === "datei"  ? !!(f.id.trim() && f.typ.trim() && f.inhalt.trim() && pruefungXml?.ok) && !dateiZuGross
    : !!(f.agentur.trim() && f.schema.trim() && f.kennung.trim());

  const senden = async () => {
    setLaeuft(true); setFehler(null);
    try {
      if (art === "eintrag")
        await api(`/api/${instId}/partner-directory/string-params`, { method:"POST",
          body: JSON.stringify({ Pid:f.pid.trim(), Id:f.id.trim(), Value:f.wert }) });
      else if (art === "param")
        await api(`/api/${instId}/partner-directory/string-params`, { method:"POST",
          body: JSON.stringify({ Pid:pid, Id:f.id.trim(), Value:f.wert }) });
      else if (art === "datei")
        await api(`/api/${instId}/partner-directory/binary-params`, { method:"POST",
          body: JSON.stringify({ Pid:pid, Id:f.id.trim(), Value:textZuB64(f.inhalt), ContentType:f.typ.trim() }) });
      else
        await api(`/api/${instId}/partner-directory/alt-partners`, { method:"POST",
          body: JSON.stringify({ Pid:pid, Agency:f.agentur.trim(), Scheme:f.schema.trim(), Id:f.kennung.trim() }) });
      aufFertig(art === "eintrag" ? f.pid.trim() : pid);
    } catch (e) {
      // Jede Anlage ist genau ein Schreibvorgang. Scheitert er, ist nichts entstanden -
      // das gehoert gesagt, sonst sucht man nach einer halben Leiche.
      setFehler(t("anlegen.fehler", { grund: e.message }));
      setLaeuft(false);
    }
  };

  // art ist die KENNUNG der Dialogart und bleibt woertlich - der Titel wird daraus geholt.
  const titel = t(`anlegen.titel.${art}`);

  const dialogRef = useRef(null);
  const schliessen = useCallback(() => { if (!laeuft) aufAbbruch(); }, [laeuft, aufAbbruch]);
  useDialogTasten(true, dialogRef, schliessen);

  return (
    <div className="iio-modal-overlay is-open"
      onMouseDown={e => { if (e.target === e.currentTarget) schliessen(); }}>
      <div className={"iio-modal" + (art === "datei" ? " pd-modal-breit" : "")} role="dialog" aria-modal="true"
        aria-labelledby="pd-anl-t" ref={dialogRef} onMouseDown={e => e.stopPropagation()}>
        <div className="iio-modal-head">
          <div className="iio-modal-head-left">
            {art !== "eintrag" && <span className="iio-modal-eyebrow">{pid}</span>}
            <h2 id="pd-anl-t">{titel}</h2>
          </div>
          <button type="button" className="iio-modal-close" onClick={schliessen} disabled={laeuft}
            aria-label={t("gemeinsam:aktion.schliessen")}><IcoX/></button>
        </div>

        <div className="iio-modal-body">
          {art === "eintrag" && <>
            <div className="iio-field">
              <label className="iio-field-label" htmlFor="pd-neu-pid">{t("anlegen.feldKennung")}</label>
              <input id="pd-neu-pid" className="iio-input pd-modal-mono" value={f.pid} autoFocus
                maxLength={GRENZE.pid}
                onChange={e => setzen("pid", e.target.value)} placeholder={t("anlegen.pidPlatzhalter")} />
              {pidDoppelt || pidReserviert
                ? <span className="pd-modal-fehler">
                    {pidReserviert ? t("anlegen.pidReserviert") : t("anlegen.pidDoppelt")}
                  </span>
                : <span className="iio-field-hint">
                    {f.pid.length >= GRENZE.pid ? t("anlegen.pidGrenze") : t("anlegen.pidHinweis")}
                  </span>}
            </div>
          </>}

          {(art === "eintrag" || art === "param") && <>
            <div className="iio-field">
              <label className="iio-field-label" htmlFor="pd-neu-id">{t("anlegen.feldParameter")}</label>
              <input id="pd-neu-id" className="iio-input pd-modal-mono" value={f.id} autoFocus={art === "param"}
                maxLength={GRENZE.paramId}
                onChange={e => setzen("id", e.target.value)} placeholder={t("anlegen.paramPlatzhalter")} />
            </div>
            <div className="iio-field">
              <label className="iio-field-label" htmlFor="pd-neu-wert">{t("anlegen.feldWert")}</label>
              <input id="pd-neu-wert" className="iio-input pd-modal-mono" value={f.wert}
                maxLength={GRENZE.paramWert}
                onChange={e => setzen("wert", e.target.value)} />
              {f.wert.length >= GRENZE.paramWert && (
                <span className="iio-field-hint">{t("anlegen.wertGrenze")}</span>
              )}
            </div>
          </>}

          {art === "datei" && <>
            <div className="iio-fields-2">
              <div className="iio-field">
                <label className="iio-field-label" htmlFor="pd-neu-fid">{t("anlegen.feldParameter")}</label>
                <input id="pd-neu-fid" className="iio-input pd-modal-mono" value={f.id} autoFocus
                  maxLength={GRENZE.paramId}
                  onChange={e => setzen("id", e.target.value)} placeholder={t("anlegen.dateiPlatzhalter")} />
              </div>
              <div className="iio-field">
                <label className="iio-field-label" htmlFor="pd-neu-typ">{t("anlegen.feldTyp")}</label>
                <input id="pd-neu-typ" className="iio-input pd-modal-mono" value={f.typ}
                  maxLength={GRENZE.typ}
                  onChange={e => setzen("typ", e.target.value)} />
              </div>
            </div>
            <div className="iio-field">
              <label className="iio-field-label" htmlFor="pd-neu-inh">{t("anlegen.feldInhalt")}</label>
              <textarea id="pd-neu-inh" className="iio-textarea pd-modal-xml" value={f.inhalt} spellCheck={false}
                wrap="off" onChange={e => setzen("inhalt", e.target.value)} />
              {dateiZuGross && (
                <span className="pd-modal-fehler">
                  {t("anlegen.dateiZuGross", { groesse: kb(dateiGroesse) })}
                </span>
              )}
              {!dateiZuGross && pruefungXml && !pruefungXml.ok && (
                <span className="pd-modal-fehler">
                  {pruefungXml.zeile ? `${pruefStelle(pruefungXml, t)}: ` : ""}
                  {pruefMeldung(pruefungXml, t)}
                </span>
              )}
              {!dateiZuGross && pruefungXml?.ok && pruefungXml.hinweis && (
                <span className="iio-field-hint">
                  {t(pruefungXml.hinweis, pruefungXml.werte)} {t("anlegen.trotzdemAnlegen")}
                </span>
              )}
            </div>
          </>}

          {art === "alt" && <>
            <div className="iio-field">
              <label className="iio-field-label" htmlFor="pd-neu-ag">{t("anlegen.feldAgentur")}</label>
              <input id="pd-neu-ag" className="iio-input pd-modal-mono" value={f.agentur} autoFocus
                maxLength={GRENZE.agentur}
                onChange={e => setzen("agentur", e.target.value)} />
            </div>
            <div className="iio-field">
              <label className="iio-field-label" htmlFor="pd-neu-sch">{t("anlegen.feldSchema")}</label>
              <input id="pd-neu-sch" className="iio-input pd-modal-mono" value={f.schema}
                maxLength={GRENZE.schema}
                onChange={e => setzen("schema", e.target.value)} />
              <span className="iio-field-hint">
                <Trans t={t} i18nKey="anlegen.schemaHinweis" components={{ b: <b/> }} />
              </span>
            </div>
            <div className="iio-field">
              <label className="iio-field-label" htmlFor="pd-neu-kn">{t("anlegen.feldAltKennung")}</label>
              <input id="pd-neu-kn" className="iio-input pd-modal-mono" value={f.kennung}
                maxLength={GRENZE.kennung}
                onChange={e => setzen("kennung", e.target.value)} />
            </div>
          </>}

          {fehler && <div className="pd-modal-fehler">{fehler}</div>}
        </div>

        <div className="iio-modal-foot">
          <div className="iio-modal-foot-left" />
          <div className="iio-modal-foot-right">
            <button type="button" className="iio-btn iio-btn--secondary" onClick={schliessen} disabled={laeuft}>{t("gemeinsam:aktion.abbrechen")}</button>
            <button type="button" className="iio-btn iio-btn--signal" onClick={senden} disabled={!bereit || laeuft}>
              {laeuft ? t("anlegen.wirdAngelegt") : t("gemeinsam:aktion.anlegen")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ── Hauptkomponente ───────────────────────────────────────────────────────── */

// k ist die KENNUNG des Reiters und bleibt woertlich (activeTab, Sichtbarkeitsregel),
// l ist die Adresse der Beschriftung im Katalog.
const REITER = [
  { k:"string", l:"reiter.string", n:d => d.stringParams.length },
  { k:"binary", l:"reiter.binary", n:d => d.binaryParams.length },
  { k:"alt",    l:"reiter.alt",    n:d => d.altPartners.length },
  { k:"users",  l:"reiter.users",  n:d => d.authorizedUsers.length },
];

/* ── Instanzwahl: Popup mit Spaces als Gruppen ──────────────────────────────── */
// Ersetzt die frueherer Auswahlliste. Kein zweiter Weg: die Liste gibt es nur hier.
// Dieselbe Komponente beantwortet auch die Frage "wohin kopieren": eyebrow/titel benennen
// die Frage, gesperrt/gruppeGesperrt begruenden, warum eine Zeile nicht in Frage kommt.
// Ohne diese vier Eigenschaften verhaelt sie sich exakt wie zuvor.
const PdInstanzWahl = ({ gruppen, aktuell, aufWahl, aufAbbruch,
                         eyebrow, titel,
                         gesperrt, gruppeGesperrt }) => {
  const { t } = useTranslation(["partnerdirectory", "gemeinsam"]);
  const kastenRef = useRef(null);
  const [filter, setFilter] = useState("");
  useDialogTasten(true, kastenRef, aufAbbruch);
  useEffect(() => {
    // Auf die gewaehlte Instanz scrollen, sonst sucht man sie bei vielen Spaces von Hand.
    const el = kastenRef.current?.querySelector(".pd-iw-item.an");
    if (el) el.scrollIntoView({ block: "center" });
    else if (kastenRef.current?.querySelector("input")) kastenRef.current.querySelector("input").focus();
    // Ist die aktuelle Instanz selbst gesperrt (Zielwahl: sie ist die Quelle), traegt der
    // erste waehlbare Eintrag den Fokus - sonst faengt die Tastatur im Nichts an.
    else kastenRef.current?.querySelector(".pd-iw-item:not(.gesperrt)")?.focus();
  }, []);

  const q = filter.trim().toLowerCase();
  const sichtbar = gruppen
    .map(([space, liste]) => [space, liste.filter(i => !q
      || i.name.toLowerCase().includes(q) || space.toLowerCase().includes(q))])
    .filter(([, liste]) => liste.length);

  return (
    <div className="iio-modal-overlay is-open"
      onMouseDown={e => { if (e.target === e.currentTarget) aufAbbruch(); }}>
      <div className="iio-modal pd-iw" role="dialog" aria-modal="true" aria-labelledby="pd-iw-t" ref={kastenRef}>
        <div className="iio-modal-head">
          <div className="iio-modal-head-left">
            <span className="iio-modal-eyebrow">{eyebrow ?? t("instanzwahl.eyebrow")}</span>
            <h2 id="pd-iw-t">{titel ?? t("instanzwahl.titel")}</h2>
          </div>
          <button type="button" className="iio-modal-close" onClick={aufAbbruch} aria-label={t("gemeinsam:aktion.schliessen")}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          </button>
        </div>
        <div className="iio-modal-body pd-iw-body">
          {gruppen.length > 1 && (
            <input className="pd-iw-suche" value={filter} onChange={e => setFilter(e.target.value)}
              aria-label={t("instanzwahl.filter")} placeholder={t("instanzwahl.filterPlatzhalter")} />
          )}
          {sichtbar.length === 0 && <div className="pd-list-empty">{t("instanzwahl.leer")}</div>}
          {sichtbar.map(([space, liste], gi) => {
            const grpGrund = gruppeGesperrt ? gruppeGesperrt(space, liste) : null;
            const grundId = `pd-iw-g${gi}`;
            return (
              <div className="pd-iw-grp" key={space}>
                <div className="pd-iw-sp">{space}</div>
                {/* Gilt der Grund fuer die ganze Gruppe, steht er einmal darunter statt in
                    jeder Zeile - acht Mal derselbe Satz ist kein Argument, sondern Laerm. */}
                {grpGrund && <div className="pd-iw-grund" id={grundId}>{grpGrund}</div>}
                {liste.map(i => {
                  const sp = gesperrt ? gesperrt(i) : null;
                  return (
                    <button type="button" key={i.id}
                      className={"pd-iw-item" + (sp ? " gesperrt" : "") + (!sp && i.id === aktuell ? " an" : "")}
                      // aria-disabled statt disabled: ein disabled-Knopf ist nicht fokussierbar,
                      // wer mit der Tastatur arbeitet, kaeme nie an den Grund.
                      aria-disabled={sp ? "true" : undefined}
                      aria-describedby={sp && grpGrund ? grundId : undefined}
                      title={sp?.satz || undefined}
                      onClick={() => { if (!sp) aufWahl(i.id); }}>
                      {sp && <IcoSchloss/>}
                      <span className="nm">{i.name}</span>
                      {/* Die Stufe steht dabei: sie entscheidet, wie vorsichtig man hier arbeitet. */}
                      {i.stage && <span className={"st" + (/prod/i.test(i.stage) ? " prod" : "")}>{i.stage.toUpperCase()}</span>}
                      {sp
                        ? <span className="grund">{sp.kurz}</span>
                        : i.id === aktuell && (
                            <span className="hk" aria-label={t("instanzwahl.ausgewaehlt")}>
                              {t("instanzwahl.ausgewaehlt")}
                            </span>
                          )}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

/* ── Rueckfrage vor dem Instanzwechsel mit stehender Auswahl ───────────────── */
// Die Auswahl gehoert zur Instanz und ist beim Wechsel weg. Bei einer einzelnen Kennung ist
// das kein Verlust - ab zwei ist es Arbeit, die niemand wortlos wegwerfen will.
const PdWechselFrage = ({ anzahl, quelleName, zielName, aufBleiben, aufWechseln }) => {
  const { t } = useTranslation(["partnerdirectory", "gemeinsam"]);
  const ref = useRef(null);
  useDialogTasten(true, ref, aufBleiben);
  return (
    <div className="iio-modal-overlay is-open"
      onMouseDown={e => { if (e.target === e.currentTarget) aufBleiben(); }}>
      <div className="iio-modal" role="dialog" aria-modal="true" aria-labelledby="pd-wf-t" ref={ref}
        onMouseDown={e => e.stopPropagation()}>
        <div className="iio-modal-head">
          <div className="iio-modal-head-left">
            <span className="iio-modal-eyebrow">{t("wechsel.eyebrow", { quelle: quelleName, ziel: zielName })}</span>
            <h2 id="pd-wf-t">{t("wechsel.titel")}</h2>
          </div>
          <button type="button" className="iio-modal-close" onClick={aufBleiben} aria-label={t("gemeinsam:aktion.schliessen")}><IcoX/></button>
        </div>
        <div className="iio-modal-body">
          <div className="pd-folge">
            <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M12 8v5M12 16h.01"/></svg>
            <span>
              <Trans t={t} i18nKey="wechsel.satz" count={anzahl}
                values={{ count: anzahl, quelle: quelleName }} components={{ b: <b/> }} />
            </span>
          </div>
        </div>
        <div className="iio-modal-foot">
          <div className="iio-modal-foot-left" />
          <div className="iio-modal-foot-right">
            <button type="button" className="iio-btn iio-btn--secondary" onClick={aufBleiben}>{t("wechsel.bleiben")}</button>
            <button type="button" className="iio-btn iio-btn--signal" onClick={aufWechseln}>{t("wechsel.wechseln")}</button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ── Kopieren: Bestaetigung, Lauf und Ergebnis in EINEM Dialog ─────────────── */

// Der Server liefert Fehler als Text mit Praefix ("String <Pid>/<Id>: …"). Zuordnen ja,
// aber nie darauf verlassen: was nicht passt, geht in "Weitere Meldungen" statt still
// zu verschwinden.
function fehlerNachPid(errors, pids) {
  const zu = new Map(pids.map(p => [p, []]));
  const rest = [];
  for (const e of errors || []) {
    const m = /^(?:String|Binary|Pid)\s+([^/:]+)/.exec(e);
    const pid = m && zu.has(m[1].trim()) ? m[1].trim() : null;
    if (pid) zu.get(pid).push(e); else rest.push(e);
  }
  return { zu, rest };
}

// Wirksamer Status eines String-Werts. Eine Anpassung fuer das Ziel verschiebt ihn: was im Ziel
// bisher identisch war, ist es nach der Anpassung nicht mehr - sonst stuende oben "sind schon
// gleich" und unten wuerde trotzdem geschrieben.
const wirkStatus = (x, angepasst) => {
  if (angepasst == null) return x.status;
  if (x.status === "create") return "create";
  return String(angepasst) === String(x.targetValue ?? "") ? "identical" : "update";
};

// Angepasst ist nur, was vom Stand der Quelle abweicht. Wer einen Wert anfasst und wieder
// zurueckschreibt, hat nichts angepasst - die Bilanz duerfte ihn sonst mitzaehlen, ohne dass
// irgendwo ein Unterschied zu sehen waere.
const istAngepasst = (x, w) => w != null && String(w) !== String(x.sourceValue ?? "");

// u = Anpassungen dieser Kennung: { string:{id:wert}, binaer:{id:text} }
const zaehleVorschau = (p, u) => {
  const us = u?.string || {}, ub = u?.binaer || {};
  const stati = [
    ...(p.stringParams || []).map(x => wirkStatus(x, us[x.Id])),
    // Binaerdateien vergleicht die Vorschau ohnehin nicht inhaltlich: eine angepasste Fassung
    // wird geschrieben, wenn die Datei im Ziel liegt (update) oder neu ist (create).
    ...(p.binaryParams || []).map(x => x.status),
    ...(p.altPartners || []).map(x => x.status),
  ];
  return {
    neu:       stati.filter(s => s === "create").length,
    ueber:     stati.filter(s => s === "update").length,
    gleich:    stati.filter(s => s === "identical").length,
    gesamt:    stati.length,
    angepasst: (p.stringParams || []).filter(x => istAngepasst(x, us[x.Id])).length
               + Object.keys(ub).length,
  };
};

const zaehleErgebnis = (r) => ({
  angelegt: (r?.strCreated || 0) + (r?.binCreated || 0) + (r?.altCreated || 0),
  ueber:    (r?.strUpdated || 0) + (r?.binUpdated || 0),
  gleich:   r?.skipped || 0,
});

const PdKopierDialog = ({ quelle, ziel, pids, entwurfPids, aufSchliessen, aufZielOeffnen }) => {
  const { t } = useTranslation(["partnerdirectory", "gemeinsam"]);
  const [vorschau, setVorschau] = useState(null);
  const [vZeit, setVZeit]       = useState(null);   // wann das Ziel gelesen wurde
  const [vLaedt, setVLaedt]     = useState(true);
  const [vFehler, setVFehler]   = useState(null);
  const [versuch, setVersuch]   = useState(0);
  const [laeuft, setLaeuft]     = useState(false);
  const [ergebnis, setErgebnis] = useState(null);
  const [kFehler, setKFehler]   = useState(null);
  const [laufPids, setLaufPids] = useState(null);   // was gerade geschickt wird
  const [offen, setOffen]       = useState(() => new Set());
  // Anpassungen NUR fuer das Ziel: { [pid]: { string:{id:wert}, binaer:{id:text} } }. Sie leben
  // ausschliesslich hier und gehen ausschliesslich in den Aufruf - die Quelle und die
  // Vorschau-Antwort bleiben unberuehrt. Ein gescheiterter Lauf laesst sie stehen.
  const [ueber, setUeber]       = useState({});
  const [inlineFeld, setInlineFeld] = useState(null);   // "pid|id" des offenen Textfelds
  const [inlineStart, setInlineStart] = useState(undefined);  // Stand beim Öffnen, fuer Escape
  const [editor, setEditor]     = useState(null);       // {pid,id} - Ansicht statt Liste
  const [edText, setEdText]     = useState(null);
  const [edBasis, setEdBasis]   = useState(null);       // Quellfassung, zum Vergleich
  const [edPruef, setEdPruef]   = useState({ ok:true });
  const [edLaedt, setEdLaedt]   = useState(false);
  const [edFehler, setEdFehler] = useState(null);
  const [verwerfenFrage, setVerwerfenFrage] = useState(false);
  // Merkt, ob die Frage vom Knopf "Zielinstanz oeffnen" kam - dann fuehrt das Verwerfen dorthin
  // und nicht ins Schliessen. Sonst haette ein Ausgang zwei verschiedene Wirkungen.
  const [zielNachFrage, setZielNachFrage] = useState(false);
  const [edFrage, setEdFrage]   = useState(false);
  const dialogRef = useRef(null);
  // Laufkennung des Datei-Abrufs. Ohne sie schreibt der Nachzuegler eines abgebrochenen
  // Abrufs seinen Inhalt in den inzwischen geoeffneten Editor einer ANDEREN Datei - und die
  // fremde Fassung landete unter fremder Kennung im Ziel-Tenant, wohlgeformt und unbemerkt.
  const edLauf = useRef(0);
  const pidsKey = pids.join("|");

  // EINE Quelle fuer alles, was mit Anpassungen zu tun hat: Zaehler, Rueckfrage und Nutzlast.
  // Was hier nicht steht, wird auch nicht geschickt - ein zurueckgetippter Wert bliebe sonst
  // in ueber stehen, waere auf dem Schirm unsichtbar und stuende trotzdem im Protokoll.
  const echteAnpassungen = useMemo(() => {
    const out = [];
    for (const p of vorschau?.partners || []) {
      const u = ueber[p.Pid]; if (!u) continue;
      for (const x of p.stringParams || [])
        if (istAngepasst(x, u.string?.[x.Id])) out.push({ pid:p.Pid, art:"string", id:x.Id, wert:u.string[x.Id] });
      for (const [id, t] of Object.entries(u.binaer || {}))
        out.push({ pid:p.Pid, art:"binaer", id, wert:t });
    }
    return out;
  }, [vorschau, ueber]);

  // Ergebnis-Ableitungen. Sie stehen hier oben, weil schon die Frage "darf dieser Dialog
  // wortlos zugehen" wissen muss, welche Kennungen noch offen sind.
  const fehler      = ergebnis ? fehlerNachPid(ergebnis.errors, ergebnis.pids) : null;
  const repNach     = ergebnis ? new Map((ergebnis.replicated || []).map(r => [r.Pid, r])) : null;
  const gescheitert = ergebnis
    ? ergebnis.pids.filter(p => (fehler.zu.get(p) || []).length || !repNach.has(p))
    : [];

  // Was verlorenginge, wenn der Dialog jetzt zuginge. Nach einem Teilfehler zaehlen nur noch
  // die Anpassungen der offenen Kennungen - "Fehlende erneut kopieren" braucht genau die.
  const offeneAnpassungen = ergebnis
    ? echteAnpassungen.filter(a => gescheitert.includes(a.pid))
    : echteAnpassungen;

  // Der getippte Stand weicht von dem ab, was uebernommen waere: dann ist der Editor nicht
  // wortlos verlassbar - ein Fehlgriff auf Escape ist nicht seltener als ein Fehlklick.
  const edGespeichert = editor ? (ueber[editor.pid]?.binaer?.[editor.id] ?? edBasis) : null;
  const edSchmutzig = !!editor && edText != null && edGespeichert != null && edText !== edGespeichert;

  // Alles, was zur offenen Datei gehoert, faellt gemeinsam weg - auch ein noch laufender
  // Abruf und die Vergleichsgrundlage. Bliebe edBasis stehen, rechnete die naechste Datei
  // gegen den Stand der vorigen.
  // Eine Frage nach dem Verwerfen von nichts ist keine Frage: wer bei offener Rueckfrage den
  // letzten angepassten Wert zuruecknimmt, bekaeme sonst "0 Werte gehen dabei verloren" mit einem
  // roten Knopf darunter.
  useEffect(() => {
    if (verwerfenFrage && offeneAnpassungen.length === 0) { setVerwerfenFrage(false); setZielNachFrage(false); }
  }, [verwerfenFrage, offeneAnpassungen]);

  const verlasseEditor = useCallback(() => {
    edLauf.current++;
    setEditor(null); setEdText(null); setEdBasis(null); setEdPruef({ ok:true });
    setEdLaedt(false); setEdFehler(null); setEdFrage(false);
  }, []);

  // Waehrend des Laufs bleibt der Dialog zu: der Aufruf ist ein einziger blockierender POST
  // ohne Zwischenstand, ein geschlossenes Fenster hinterliesse einen halb bekannten
  // Schreibzustand in einem fremden Tenant.
  const schliessen = useCallback(() => {
    if (laeuft) return;
    // Sauber ist nur, was ohne eine einzige Meldung durchlief UND fuer jede geschickte
    // Kennung eine Rueckmeldung hat - nur dann darf die Auswahl draussen verschwinden.
    const gemeldet = new Set(((ergebnis?.replicated) || []).map(r => r.Pid));
    aufSchliessen(!!ergebnis && !(ergebnis.errors || []).length
      && ergebnis.pids.every(p => gemeldet.has(p)));
  }, [laeuft, ergebnis, aufSchliessen]);
  // Escape fuehrt genau eine Ebene zurueck, nicht durch alles hindurch: eine offene Frage,
  // dann den Editor, dann den Dialog - und waehrend des Laufs gar nichts, sonst stuende eine
  // Frage auf dem Schirm, deren Knopf nichts tut.
  const zurueckOderZu = useCallback(() => {
    if (laeuft) return;
    if (edFrage) { setEdFrage(false); return; }
    if (verwerfenFrage) { setVerwerfenFrage(false); setZielNachFrage(false); return; }
    if (editor) { if (edSchmutzig) setEdFrage(true); else verlasseEditor(); return; }
    if (offeneAnpassungen.length > 0) { setVerwerfenFrage(true); return; }
    schliessen();
  }, [laeuft, edFrage, verwerfenFrage, editor, edSchmutzig, offeneAnpassungen, schliessen, verlasseEditor]);
  useDialogTasten(true, dialogRef, zurueckOderZu);

  // Dieselbe Zusage wie in der Detailspalte: ungesicherte Arbeit geht nicht still verloren.
  useEffect(() => {
    if (!offeneAnpassungen.length && !edSchmutzig) return undefined;
    const h = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [offeneAnpassungen, edSchmutzig]);

  useEffect(() => {
    if (ergebnis) return undefined;
    let ab = false;
    setVLaedt(true); setVFehler(null);
    api("/api/transport/partner-directory-preview", { method:"POST",
      body: JSON.stringify({ sourceInstanceId: quelle.id, targetInstanceId: ziel.id, pids }) })
      .then(d => { if (!ab) { setVorschau(d); setVZeit(uhrzeit()); setVLaedt(false); } })
      .catch(e => { if (!ab) { setVFehler(e.message); setVLaedt(false); } });
    return () => { ab = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quelle.id, ziel.id, pidsKey, versuch]);

  // Nur die Anpassungen der Kennungen, die dieser Lauf mitnimmt, und nur die echten. Base64
  // ueber textZuB64, damit Umlaute in einer Zielfassung dieselbe Kodierung haben wie in der
  // Detailspalte.
  const nutzlast = (welche) => {
    const dabei = new Set(welche);
    const out = {};
    for (const a of echteAnpassungen) {
      if (!dabei.has(a.pid)) continue;
      const teil = out[a.pid] || (out[a.pid] = {});
      const fach = teil[a.art] || (teil[a.art] = {});
      fach[a.id] = a.art === "binaer" ? textZuB64(a.wert) : a.wert;
    }
    return out;
  };

  const kopiere = async (welche) => {
    // Ein bereits offenes Eingabefeld und ein offener Editor werden beim Start geschlossen:
    // die Nutzlast steht mit diesem Aufruf fest, alles danach Getippte ginge nicht mehr mit -
    // stehenbleiben wuerde also eine Aenderung vortaeuschen, die den Tenant nie erreicht.
    setInlineFeld(null); setInlineStart(undefined);
    if (editor) verlasseEditor();
    setLaeuft(true); setKFehler(null); setLaufPids(welche);
    // Die geschickten Anpassungen als LISTE, nicht als Zahl: welche davon wirklich ankamen,
    // steht erst fest, wenn die gescheiterten Kennungen bekannt sind.
    const mit = echteAnpassungen.filter(a => welche.includes(a.pid)).map(a => a.pid);
    try {
      const r = await api(`/api/${quelle.id}/partner-directory/transport`, { method:"POST",
        body: JSON.stringify({ targetInstanceId: ziel.id, pids: welche, ueberschreibungen: nutzlast(welche) }) });
      setErgebnis({ ...r, pids: welche, angepasstPids: mit });
      setOffen(new Set());
    } catch (e) { setKFehler(e.message); }
    setLaeuft(false);
  };

  const klapp = (pid) => setOffen(s => {
    const n = new Set(s); if (n.has(pid)) n.delete(pid); else n.add(pid); return n;
  });

  const setzeString = (pid, id, wert) => setUeber(u => {
    const p = u[pid] || {};
    return { ...u, [pid]: { ...p, string: { ...(p.string || {}), [id]: wert } } };
  });

  // Escape nimmt zurueck, Enter und "Fertig" uebernehmen - dieselbe Bedeutung wie im
  // Eingabefeld der Detailspalte. Was am Ende dem Quellwert entspricht, ist keine Anpassung
  // und verlaesst ueber wieder.
  const inlineOeffnen = (pid, x) => {
    if (laeuft) return;   // waehrend des Laufs aendert sich nichts mehr an dem, was geschrieben wird
    // Wer weiterarbeitet, hat die Verwerfen-Frage beantwortet. Bliebe sie unsichtbar stehen,
    // raeumte der naechste Klick auf "Abbrechen" nur sie ab und schiene wirkungslos.
    setVerwerfenFrage(false); setZielNachFrage(false);
    setInlineStart(ueber[pid]?.string?.[x.Id]);
    setInlineFeld(`${pid}|${x.Id}`);
  };
  const inlineZurueck = (pid, x) => {
    if (inlineStart === undefined) nimmZurueck(pid, "string", x.Id);
    else setzeString(pid, x.Id, inlineStart);
    setInlineFeld(null);
  };
  const inlineFertig = (pid, x) => {
    if (!istAngepasst(x, ueber[pid]?.string?.[x.Id])) nimmZurueck(pid, "string", x.Id);
    setInlineFeld(null);
  };

  const nimmZurueck = (pid, art, id) => setUeber(u => {
    const p = u[pid]; if (!p?.[art]) return u;
    const rest = { ...p[art] }; delete rest[id];
    const neu = { ...p, [art]: rest };
    const out = { ...u };
    if (!Object.keys(neu.string || {}).length && !Object.keys(neu.binaer || {}).length) delete out[pid];
    else out[pid] = neu;
    return out;
  });

  // Der Ausgangswert ist der Stand der QUELLE - der wuerde ohne Anpassung geschrieben.
  const oeffneEditor = async (pid, x) => {
    if (laeuft) return;
    setVerwerfenFrage(false); setZielNachFrage(false);   // siehe inlineOeffnen
    const lauf = ++edLauf.current;
    const schon = ueber[pid]?.binaer?.[x.Id];
    setEditor({ pid, id: x.Id }); setEdFehler(null); setEdBasis(null);
    setEdLaedt(true); setEdText(schon ?? null); setEdPruef(schon != null ? pruefeXml(schon) : { ok:true });
    try {
      const d = await api(`/api/${quelle.id}/partner-directory/${encodeURIComponent(pid)}`);
      if (edLauf.current !== lauf) return;   // inzwischen andere Datei oder Editor verlassen
      const bp = (d.binaryParams || []).find(b => b.Id === x.Id);
      const quelltext = bp?.Value ? b64ZuText(bp.Value) : "";
      setEdBasis(quelltext);
      if (schon == null) { setEdText(quelltext); setEdPruef(pruefeXml(quelltext)); }
    } catch (e) {
      if (edLauf.current !== lauf) return;
      setEdFehler(t("kopieren.editorLadefehler", { quelle: quelle.name, grund: e.message }));
      if (schon == null) setEdText(null);
    }
    if (edLauf.current === lauf) setEdLaedt(false);
  };

  // Entprellt wie in der Detailspalte - der DOMParser ist zu teuer fuer jeden Tastendruck.
  useEffect(() => {
    if (!editor || edText == null) return undefined;
    const t = setTimeout(() => setEdPruef(pruefeXml(edText)), 400);
    return () => clearTimeout(t);
  }, [edText, editor]);

  // PRE-PROD traegt bei vielen Kunden echte Daten und wird deshalb wie PROD gewarnt.
  const istProdZiel = istProdNah(ziel.stage);
  const zeilenPids  = ergebnis ? ergebnis.pids : pids;

  // Die Bilanz wird HIER gerechnet, nicht aus vorschau.summary uebernommen: der Server kennt
  // die Anpassungen nicht, und ein angepasster Wert, der im Ziel bisher identisch war, ist es
  // nicht mehr. Sonst stuende oben "sind schon gleich" und unten wuerde trotzdem geschrieben.
  const s = useMemo(() => {
    if (!vorschau) return null;
    let create = 0, update = 0, identical = 0, angepasst = 0;
    for (const p of vorschau.partners || []) {
      const z = zaehleVorschau(p, ueber[p.Pid]);
      create += z.neu; update += z.ueber; identical += z.gleich; angepasst += z.angepasst;
    }
    return { create, update, identical, angepasst };
  }, [vorschau, ueber]);

  const edGroesse = new TextEncoder().encode(edText || "").length;
  const edUnveraendert = edText != null && edBasis != null && edText === edBasis
    && ueber[editor?.pid]?.binaer?.[editor?.id] == null;

  const bilanz = () => {
    // "Kein Eintrag wurde kopiert" gilt nur, wenn noch nie einer ankam. Steht darunter bereits
    // eine Ergebnisliste, waere der Satz eine Behauptung gegen die eigene Anzeige.
    if (kFehler && !ergebnis) return (
      <div className="pd-kop-bilanz">
        {t("kopieren.keinerKopiert")}
        <span className="neben">{kFehler}</span>
      </div>
    );
    if (ergebnis) {
      let a = 0, u = 0, g = 0;
      for (const r of ergebnis.replicated || []) {
        const z = zaehleErgebnis(r); a += z.angelegt; u += z.ueber; g += z.gleich;
      }
      const n  = ergebnis.pids.length;
      const ok = n - gescheitert.length;
      // Dass angepasst geschrieben wurde, gehoert auch nach dem Lauf gesagt: wer spaeter
      // Quelle und Ziel vergleicht, findet sonst einen Unterschied ohne Erklaerung. Gezaehlt
      // wird nur, was auch ankam - bei einer gescheiterten Kennung ist nichts geschrieben.
      const ang = (ergebnis.angepasstPids || []).filter(p => !gescheitert.includes(p)).length;
      const angepasstZeile = ang > 0 && (
        <span className="neben">
          {t("kopieren.angepasstGeschrieben", { count: ang, ziel: ziel.name })}
        </span>
      );
      if (gescheitert.length === 0 && !(fehler.rest || []).length) return (
        <div className="pd-kop-bilanz">
          <b>{n}</b> {t("kopieren.bilanzKopiert", { count: n })}
          <span className="neben">
            {a + u === 0
              ? t("kopieren.nichtsZuTun")
              : t("kopieren.bilanzWerte", { a, u, g })}
          </span>
          {angepasstZeile}
        </div>
      );
      return (
        <div className="pd-kop-bilanz">
          {ok > 0
            ? <Trans t={t} i18nKey="kopieren.bilanzTeil" count={n} values={{ ok, count: n }} components={{ b: <b/> }} />
            : t("kopieren.keinerVollstaendig")}
          <span className="neben">
            {gescheitert.length === 1
              ? t("kopieren.teilEine", { pid: gescheitert[0] })
              : gescheitert.length > 1
                ? t("kopieren.teilMehrere", { count: gescheitert.length })
                : t("kopieren.zusatzMeldungen")}
          </span>
          {angepasstZeile}
        </div>
      );
    }
    if (vLaedt) return <div className="pd-kop-bilanz">{t("kopieren.zielWirdGeprueft")}</div>;
    // Waehrend des Laufs kein Futur mehr: geschrieben wird der Stand von vorhin, nicht der,
    // den die Zeile gerade beschreibt.
    if (laeuft) return <div className="pd-kop-bilanz">{t("kopieren.laufHinweis")}</div>;
    if (vFehler) return null;
    if (!s) return null;
    return (
      <div className="pd-kop-bilanz">
        {/* Drei Zahlen, drei eigene Beugungen: i18next kennt genau ein count je Schluessel,
            deshalb kommen die Wortformen einzeln herein und der Satz haelt sie zusammen. */}
        <Trans t={t} i18nKey="kopieren.vorschauBilanz" components={{ b: <b/> }} values={{
          create: s.create, update: s.update, identical: s.identical,
          vCreate: t("kopieren.vorschauCreate", { count: s.create }),
          vUpdate: t("kopieren.vorschauUpdate", { count: s.update }),
          vIdentical: t("kopieren.vorschauIdentical", { count: s.identical }),
        }} />
        {/* Die Vorschau ist eine Momentaufnahme ohne Ueberschreibschutz: was zwischen Lesen und
            Kopieren im Ziel passiert, sieht sie nicht. Deshalb steht die Uhrzeit dabei. */}
        <span className="neben">
          {t("kopieren.vorschauStand", { zeit: vZeit })}
        </span>
        {/* Wer etwas angepasst hat, soll es nicht vergessen, bevor er kopiert. */}
        {s.angepasst > 0 && (
          <span className="neben">
            <Trans t={t} i18nKey="kopieren.angepasstHinweis" count={s.angepasst}
              values={{ count: s.angepasst, ziel: ziel.name, quelle: quelle.name }}
              components={{ b: <b/> }} />
          </span>
        )}
      </div>
    );
  };

  return (
    <div className="iio-modal-overlay is-open"
      // Der Aussenklick geht denselben Weg wie Escape: er wirft nichts weg, aber er bleibt
      // auch nicht wirkungslos - ist etwas zu verlieren, stellt er die Frage.
      onMouseDown={e => { if (e.target === e.currentTarget) zurueckOderZu(); }}>
      <div className={"iio-modal pd-kop" + (editor ? " mit-editor" : "")} role="dialog" aria-modal="true"
        ref={dialogRef} aria-labelledby="pd-kop-t" onMouseDown={e => e.stopPropagation()}>
        <div className="iio-modal-head">
          <div className="iio-modal-head-left">
            <span className="iio-modal-eyebrow">{t("kopieren.vonNach", { quelle: quelle.name, ziel: ziel.name })}</span>
            <h2 id="pd-kop-t">{editor ? t("kopieren.titelEditor") : t("kopieren.titel")}</h2>
          </div>
          {/* Das Kreuz geht denselben Weg wie Escape und der Aussenklick: bei offener Frage nimmt
              es sie zurueck, sonst schliesst es. Es wirft nie etwas weg, ohne vorher zu fragen -
              deshalb muss die Beschriftung mitziehen, sonst sagt sie "Schliessen" und tut es nicht. */}
          <button type="button" className="iio-modal-close" onClick={zurueckOderZu}
            disabled={laeuft}
            aria-label={verwerfenFrage || edFrage ? t("kopieren.ariaFrageZurueck")
              : editor ? t("kopieren.ariaZurueckUebersicht") : t("gemeinsam:aktion.schliessen")}><IcoX/></button>
        </div>

        {/* Der Editor ersetzt die Uebersicht, statt neben ihr aufzugehen: eine Frage je Ansicht,
            und das XSLT bekommt die ganze Flaeche. */}
        {editor && (
          <div className="iio-modal-body">
            <div className="pd-kop-edkopf">
              <span className="id">{editor.id}</span><span>{t("kopieren.editorIn", { pid: editor.pid })}</span>
            </div>
            <div className="pd-folge">
              <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M12 8v5M12 16h.01"/></svg>
              <span>
                <Trans t={t} i18nKey="kopieren.editorFolge"
                  values={{ ziel: ziel.name, quelle: quelle.name }} components={{ b: <b/> }} />
              </span>
            </div>
            {edFehler && <div className="pd-msg bad"><span>{edFehler}</span></div>}
            {edLaedt && edText == null && <div className="pd-kop-bilanz">{t("kopieren.editorLaedt")}</div>}
            {edText != null && (
              <div className="pd-kop-ed">
                <PdXmlFeld wert={edText} setzeWert={setEdText} fehlerZeile={edPruef.zeile}
                  label={t("kopieren.ariaZielfassung", { id: editor.id })} />
                <PdXmlStatus pruefung={edPruef} groesse={edGroesse} zweck="uebernehmen" />
              </div>
            )}
          </div>
        )}

        {!editor && <div className="iio-modal-body">
          {istProdZiel && !ergebnis && (
            <div className="pd-folge prod">
              <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M12 8v5M12 16h.01"/></svg>
              <span>{t("kopieren.prodZiel")}</span>
            </div>
          )}

          {!ergebnis && !laeuft && entwurfPids.length > 0 && (
            <div className="pd-msg warn">
              <span>
                {/* Bei genau einer Kennung steht ihr Name im Satz - er ist ein Datenwert und
                    geht als Platzhalter hinein, nicht als Textbaustein. */}
                {entwurfPids.length === 1
                  ? t("kopieren.entwurfEine", { pid: entwurfPids[0] })
                  : t("kopieren.entwurfMehrere", { count: entwurfPids.length })}
              </span>
            </div>
          )}

          {!ergebnis && !laeuft && pids.length > 25 && (
            <div className="pd-msg">
              <span>{t("kopieren.vieleEintraege", { count: pids.length })}</span>
            </div>
          )}

          {vFehler && !ergebnis && (
            <div className="pd-msg bad">
              <span>{t("kopieren.zielUnpruefbar", { grund: vFehler })}</span>
              <button type="button" className="pd-btn ghost sm re" onClick={() => setVersuch(v => v + 1)}>
                {t("kopieren.erneutPruefen")}
              </button>
            </div>
          )}

          {bilanz()}

          {kFehler && ergebnis && (
            <div className="pd-msg bad">
              <span>{t("kopieren.wiederholungFehler", { grund: kFehler })}</span>
            </div>
          )}

          {laeuft && (
            <div className="pd-kop-lauf">
              <span className="txt">
                {t("kopieren.laufText", { count: (laufPids || pids).length })}
              </span>
              <span className="schiene"><i /></span>
            </div>
          )}

          <div className="pd-kop-liste">
            {vLaedt && !ergebnis && [...Array(3)].map((_, i) => <div key={i} className="pd-kop-skel" />)}

            {!vLaedt && !ergebnis && (vorschau?.partners || []).map(p => {
              const u  = ueber[p.Pid];
              const us = u?.string || {}, ub = u?.binaer || {};
              const z  = zaehleVorschau(p, u);
              // Jede Zeile mit Inhalt laesst sich aufklappen, nicht nur die mit Ueberschreibung:
              // seit die Zielwerte anpassbar sind, ist auch ein neuer Wert einen Blick wert.
              // Ohne Inhalt bleibt der Satz zum unlesbaren Ziel der einzige Grund.
              const aufklappbar = z.gesamt > 0 || !!p.targetReadError;
              const auf = offen.has(p.Pid);
              const bar = p.targetReadError || z.ueber > 0 ? "ueber" : z.neu > 0 ? "neu" : "";
              const Kopf = aufklappbar ? "button" : "div";
              return (
                <div key={p.Pid} className={"pd-kop-zeile" + (auf ? " offen" : "")}>
                  <Kopf className="pd-kop-kopf" {...(aufklappbar
                    ? { type:"button", onClick:() => klapp(p.Pid), "aria-expanded":auf } : {})}>
                    <span className={"bar " + bar} />
                    <span className="pid" title={p.Pid}>{p.Pid}</span>
                    <span className="folge">
                      {p.targetReadError
                        ? <span className="warn">{t("kopieren.zielUnlesbar")}</span>
                        : z.gesamt === 0 ? t("kopieren.ohneInhalt")
                        : !p.targetExists ? t("kopieren.neuImZiel", { count: z.gesamt })
                        : z.neu === 0 && z.ueber === 0 ? t("kopieren.unveraendert")
                        : <>
                            {z.neu > 0 && t("kopieren.zNeu", { count: z.neu })}
                            {z.neu > 0 && z.ueber > 0 && " · "}
                            {z.ueber > 0 && (
                              <span className="warn">{t("kopieren.zUeber", { count: z.ueber })}</span>
                            )}
                          </>}
                      {z.angepasst > 0 && <> · <span className="ang">{t("kopieren.zAngepasst", { count: z.angepasst })}</span></>}
                    </span>
                    {aufklappbar
                      ? <svg className="chev" viewBox="0 0 24 24"><polyline points="9 6 15 12 9 18"/></svg>
                      : <span />}
                  </Kopf>

                  {auf && (
                    <div className="pd-kop-werte">
                      {p.targetReadError && (
                        <div className="pd-kop-wert">
                          <span className="hinw">{t("kopieren.zielUnlesbarSatz")}</span>
                        </div>
                      )}
                      {(p.stringParams || []).length > 0 && (
                        <div className="pd-kop-wkopf">
                          <span>{t("kopieren.spalteParameter")}</span>
                          <span>{t("kopieren.spalteWertNachher")}</span>
                          <span>{t("kopieren.spalteAnpassen")}</span>
                        </div>
                      )}
                      {(p.stringParams || []).map(x => {
                        const w   = us[x.Id];                       // angepasst oder undefined
                        const eff = w != null ? w : x.sourceValue;  // was geschrieben wird
                        const st  = wirkStatus(x, w);
                        const schl = `${p.Pid}|${x.Id}`;
                        const zeigtAlt = x.status !== "create" && st !== "identical";
                        return (
                          <div className="pd-kop-wert werkzeug" key={"s" + x.Id}>
                            <span className="k">{x.Id}</span>
                            <span className="paar">
                              {/* Der bisherige Zielwert bleibt auch beim Tippen stehen - man
                                  aendert ihn ja gerade gegen genau diesen Stand. Trifft der
                                  getippte Wert ihn, faellt er weg: dann wird nichts ersetzt. */}
                              {zeigtAlt && <>
                                <span className="alt">{x.targetValue || t("wert.leer")}</span>
                                <span className="pfeil" aria-hidden="true">→</span>
                              </>}
                              {inlineFeld === schl
                                ? <input className="edit" value={eff || ""} autoFocus maxLength={GRENZE.paramWert}
                                    aria-label={t("kopieren.ariaZielwert", { id: x.Id })}
                                    onChange={e => setzeString(p.Pid, x.Id, e.target.value)}
                                    onKeyDown={e => {
                                      if (e.key === "Enter")  { e.stopPropagation(); inlineFertig(p.Pid, x); }
                                      if (e.key === "Escape") { e.stopPropagation(); inlineZurueck(p.Pid, x); }
                                    }} />
                                : <>
                                    <span className={st === "identical" ? undefined : "neu"}>{eff || t("wert.leer")}</span>
                                    {istAngepasst(x, w) && <span className="angepasst">{t("kopieren.fuerZielAngepasst")}</span>}
                                  </>}
                            </span>
                            <span className="werkz">
                              {inlineFeld === schl
                                ? <button type="button" className="pd-btn ghost sm"
                                    onClick={() => inlineFertig(p.Pid, x)}>{t("kopieren.fertig")}</button>
                                : <button type="button" className="pd-ic" disabled={laeuft}
                                    title={laeuft ? t("kopieren.titelWaehrendLauf") : t("kopieren.titelAnpassen")}
                                    aria-label={t("kopieren.ariaZielwertAnpassen", { id: x.Id })}
                                    onClick={() => inlineOeffnen(p.Pid, x)}><IcoStift/></button>}
                              {istAngepasst(x, w) && (
                                <button type="button" className="pd-ic" disabled={laeuft} title={t("kopieren.anpassungZurueck")}
                                  aria-label={t("kopieren.ariaAnpassungZurueck", { id: x.Id })}
                                  onClick={() => { nimmZurueck(p.Pid, "string", x.Id); setInlineFeld(null); }}>
                                  <IcoZurueck/>
                                </button>
                              )}
                            </span>
                          </div>
                        );
                      })}
                      {(p.binaryParams || []).map(x => {
                        const angepasst = ub[x.Id] != null;
                        return (
                          <div className="pd-kop-wert werkzeug" key={"b" + x.Id}>
                            <span className="k">{x.Id}</span>
                            {/* Die Vorschau vergleicht Base64 NICHT inhaltlich - das gehoert gesagt,
                                sonst liest man "wird überschrieben" als "hat sich geändert". */}
                            <span className="hinw sp2">
                              {angepasst
                                ? <Trans t={t} i18nKey="kopieren.binAngepasst"
                                    components={{ b: <b className="angepasst-satz"/> }} />
                                : x.status === "update"
                                  ? t("kopieren.binErsetzt")
                                  : t("kopieren.binNeu")}
                            </span>
                            <span className="werkz">
                              <button type="button" className="pd-ic" disabled={laeuft}
                                title={laeuft ? t("kopieren.titelWaehrendLauf") : t("kopieren.titelAnpassen")}
                                aria-label={t("kopieren.ariaZielfassungAnpassen", { id: x.Id })}
                                onClick={() => oeffneEditor(p.Pid, x)}><IcoStift/></button>
                              {angepasst && (
                                <button type="button" className="pd-ic" disabled={laeuft} title={t("kopieren.anpassungZurueck")}
                                  aria-label={t("kopieren.ariaAnpassungZurueck", { id: x.Id })}
                                  onClick={() => nimmZurueck(p.Pid, "binaer", x.Id)}><IcoZurueck/></button>
                              )}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}

            {ergebnis && zeilenPids.map(pid => {
              const r  = repNach.get(pid);
              const ez = zaehleErgebnis(r);
              const ef = fehler.zu.get(pid) || [];
              const kaputt = ef.length > 0 || !r;
              const auf = offen.has(pid);
              const Kopf = kaputt ? "button" : "div";
              return (
                <div key={pid} className={"pd-kop-zeile" + (auf ? " offen" : "")}>
                  <Kopf className="pd-kop-kopf" {...(kaputt
                    ? { type:"button", onClick:() => klapp(pid), "aria-expanded":auf } : {})}>
                    <span className={"bar " + (kaputt ? "fehl" : ez.angelegt + ez.ueber > 0 ? "neu" : "")} />
                    <span className="pid" title={pid}>{pid}</span>
                    <span className="folge">
                      {!r
                        ? <span className="bad">{t("kopieren.nichtKopiert")}</span>
                        : <>
                            {ez.angelegt > 0 && t("kopieren.eAngelegt", { count: ez.angelegt })}
                            {ez.angelegt > 0 && ez.ueber > 0 && " · "}
                            {ez.ueber > 0 && t("kopieren.eUeber", { count: ez.ueber })}
                            {ez.angelegt === 0 && ez.ueber === 0 && t("kopieren.unveraendert")}
                            {ef.length > 0 && <> · <span className="bad">{t("kopieren.eNichtUebernommen", { count: ef.length })}</span></>}
                          </>}
                    </span>
                    {kaputt
                      ? <svg className="chev" viewBox="0 0 24 24"><polyline points="9 6 15 12 9 18"/></svg>
                      : <span />}
                  </Kopf>
                  {auf && (
                    <div className="pd-kop-werte">
                      {/* Rohtext des Servers, unveraendert. Umformulieren waere Raten. */}
                      {ef.map((m, i) => <div key={i} className="pd-kop-fehler">{m}</div>)}
                      {!ef.length && !r && (
                        <div className="pd-kop-fehler">{t("kopieren.keineRueckmeldung")}</div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {ergebnis && (fehler.rest || []).length > 0 && (
            <div>
              <div className="pd-kop-wkopf" style={{padding:"0 0 4px"}}>{t("kopieren.weitereMeldungen")}</div>
              {fehler.rest.map((m, i) => <div key={i} className="pd-kop-fehler">{m}</div>)}
            </div>
          )}
        </div>}

        {editor && edFrage ? (
          <div className="iio-modal-foot" key="ed-frage">
            <div className="iio-modal-foot-left">
              <span className="pd-kop-verwerfen">{t("kopieren.edVerlust")}</span>
            </div>
            <div className="iio-modal-foot-right">
              <button type="button" className="iio-btn iio-btn--secondary"
                onClick={() => setEdFrage(false)}>{t("gemeinsam:aktion.zurueck")}</button>
              <button type="button" className="iio-btn iio-btn--danger"
                onClick={verlasseEditor}>{t("detail.verwerfen")}</button>
            </div>
          </div>
        ) : editor ? (
          <div className="iio-modal-foot" key="ed-normal">
            <div className="iio-modal-foot-left">
              {/* Zuruecknehmen ist die Nebenhandlung und steht getrennt von Abbrechen:
                  Abbrechen laesst eine bestehende Anpassung stehen, Zuruecknehmen loescht sie. */}
              {ueber[editor.pid]?.binaer?.[editor.id] != null && (
                <button type="button" className="pd-kop-fuss-link"
                  onClick={() => { nimmZurueck(editor.pid, "binaer", editor.id); verlasseEditor(); }}>
                  {t("kopieren.anpassungZurueck")}
                </button>
              )}
            </div>
            <div className="iio-modal-foot-right">
              <button type="button" className="iio-btn iio-btn--secondary"
                onClick={zurueckOderZu}>{t("gemeinsam:aktion.abbrechen")}</button>
              <button type="button" className="iio-btn iio-btn--signal"
                disabled={edText == null || !edPruef.ok || edGroesse > GRENZE.datei || edUnveraendert}
                onClick={() => {
                  const { pid, id } = editor; const t = edText;
                  // Wer eine bestehende Anpassung auf die Quellfassung zurueckschreibt, hat sie
                  // zurueckgenommen - sie weiter zu fuehren hiesse, dem Protokoll etwas zu
                  // melden, das sich nirgends unterscheidet.
                  if (edBasis != null && t === edBasis) nimmZurueck(pid, "binaer", id);
                  else setUeber(u => {
                    const p = u[pid] || {};
                    return { ...u, [pid]: { ...p, binaer: { ...(p.binaer || {}), [id]: t } } };
                  });
                  verlasseEditor();
                }}>
                {t("kopieren.fuerZielUebernehmen")}
              </button>
            </div>
          </div>
        ) : verwerfenFrage ? (
          // Eigener key: sonst beschriftet React denselben Knopf nur um, statt ihn zu tauschen.
          // Schliesst der Effekt die Frage genau zwischen Maustaste runter und hoch - moeglich,
          // wenn eine laufende Neupruefung die Anpassungen fallen laesst -, landete der Klick auf
          // dem Knopf, der dann dort steht: "Kopieren". Mit getauschtem Knoten loest er nichts aus.
          <div className="iio-modal-foot" key="frage">
            <div className="iio-modal-foot-left">
              <span className="pd-kop-verwerfen">
                {/* Vier ganze Saetze statt zusammengesetzter Teile: der Nachsatz haengt im
                    Deutschen wie im Englischen am Hauptsatz, nicht an einem Komma. */}
                {t(ergebnis ? "kopieren.verlustVorRest" : "kopieren.verlust",
                  { count: offeneAnpassungen.length })}
              </span>
            </div>
            <div className="iio-modal-foot-right">
              <button type="button" className="iio-btn iio-btn--secondary"
                onClick={() => { setVerwerfenFrage(false); setZielNachFrage(false); }}>{t("gemeinsam:aktion.zurueck")}</button>
              <button type="button" className="iio-btn iio-btn--danger"
                onClick={() => { setVerwerfenFrage(false); if (zielNachFrage) { setZielNachFrage(false); aufZielOeffnen(); } else schliessen(); }}>
                {zielNachFrage ? t("kopieren.verwerfenUndZiel") : t("kopieren.verwerfenUndSchliessen")}</button>
            </div>
          </div>
        ) : (
        <div className="iio-modal-foot" key="normal">
          <div className="iio-modal-foot-left">
            {ergebnis
              ? <button type="button" className="pd-kop-fuss-link"
                  onClick={() => { if (offeneAnpassungen.length > 0) { setZielNachFrage(true); setVerwerfenFrage(true); return; } aufZielOeffnen(); }}>
                  {t("kopieren.zielOeffnen")}
                </button>
              : !vLaedt && !vFehler && (
                  <span style={{fontSize:12, color:"var(--iio-text-3)"}}>
                    {t("kopieren.eintraege", { count: pids.length })}
                  </span>
                )}
          </div>
          <div className="iio-modal-foot-right">
            <button type="button" className="iio-btn iio-btn--secondary" onClick={zurueckOderZu} disabled={laeuft}>
              {ergebnis ? t("gemeinsam:aktion.schliessen") : t("gemeinsam:aktion.abbrechen")}
            </button>
            {!ergebnis && (
              <button type="button" className="iio-btn iio-btn--signal" disabled={laeuft || vLaedt || !!vFehler}
                onClick={() => kopiere(pids)}>
                {laeuft ? t("kopieren.wirdKopiert") : kFehler ? t("kopieren.erneutVersuchen") : t("kopieren.kopieren")}
              </button>
            )}
            {ergebnis && gescheitert.length > 0 && (
              <button type="button" className="iio-btn iio-btn--signal" disabled={laeuft}
                onClick={() => kopiere(gescheitert)}>
                {t("kopieren.fehlendeErneut")}
              </button>
            )}
          </div>
        </div>
        )}
      </div>
    </div>
  );
};

// Der Backend-Endpunkt nimmt nur diese Zeichen an. Was er ablehnen wuerde, ist gar nicht
// erst waehlbar - eine Fehlermeldung nach dem Klick waere die schlechtere Auskunft.
// Kopierbar ist eine Kennung, wenn sie technisch adressierbar ist UND der Server sie nicht als
// systemeigen meldet (`systemGrund`). Die Sperrliste steht ausschliesslich im Backend - haette die
// Oberflaeche eine eigene, liefen Anzeige und Schranke beim naechsten Eintrag auseinander.
const KOPIERBAR = (p) => /^[\w\-.]+$/.test(String(p?.Pid || "")) && !p?.systemGrund;

const PartnerDirectory = ({ spaces, preselectedInstId, preselectedPid }) => {
  const { t } = useTranslation(["partnerdirectory", "gemeinsam"]);
  const [instances, setInstances]   = useState([]);
  const [selectedInst, setSelInst]  = useState("");
  const [instWahlOffen, setInstWahlOffen] = useState(false);
  const [recht, setRecht]           = useState(null);   // null = noch unbekannt
  const [partners, setPartners]     = useState([]);
  const [loading, setLoading]       = useState(false);
  const [listFehler, setListFehler] = useState(null);
  // Der HTTP-Status statt des Meldungstextes: der Text ist seit dem
  // Uebersetzungsfilter sprachabhaengig, der Status nicht.
  const [listStatus, setListStatus] = useState(null);
  const [selectedPid, setSelPid]    = useState(null);
  const [pidDetail, setPidDetail]   = useState(null);
  const [pidLaedt, setPidLaedt]     = useState(false);
  const [detailFehler, setDetailFehler] = useState(null);
  const [suche, setSuche]           = useState("");
  const [activeTab, setActiveTab]   = useState("string");
  const [anlegen, setAnlegen]       = useState(null);   // "eintrag"|"param"|"datei"|"alt"
  const [arbeit, setArbeit]         = useState({});   // Bearbeitungsstand je "Pid|Parameter"
  const [wunschPid, setWunschPid]   = useState(null);   // Kennung aus der Vorauswahl, noch nicht aufgeloest
  const [wunschFehler, setWunschFehler] = useState(null);
  const [waehlend, setWaehlend]     = useState(false);  // Auswahlmodus zum Kopieren
  const [auswahl, setAuswahl]       = useState(() => new Set());
  const [zielWahlOffen, setZielWahlOffen] = useState(false);
  const [zielRecht, setZielRecht]   = useState({});     // instId -> true|false, fehlt = wird geprueft
  const [kopierZiel, setKopierZiel] = useState(null);   // gewaehlte Zielinstanz, Dialog offen
  const [wechselFrage, setWechselFrage] = useState(null); // Instanzwechsel mit stehender Auswahl
  const rowsRef = useRef(null);
  const detailLauf = useRef(0);
  const letzteVorauswahl = useRef({ inst:null, pid:null });

  // Kein Schreibrecht, bis es erwiesen ist. Solange die Auskunft aussteht oder scheitert,
  // bleibt die Oberflaeche lesend - ein zu frueh angebotener Knopf endet sonst im 403.
  const darfSchreiben = recht?.transport === true;
  // Im Auswahlmodus ruht die Detailspalte. Sie bleibt lesbar - sie ist der einzige Ort, an dem
  // man VOR dem Ueberschreiben nachsieht, was in einem Eintrag steht -, aber ohne Schreibwerkzeug:
  // sonst stuende ein ungespeicherter Entwurf auf dem Schirm, waehrend der Kopiervorgang den
  // gespeicherten Stand aus dem Tenant liest.
  const darfBearbeiten = darfSchreiben && !waehlend;
  const instanz = useMemo(() => instances.find(i => i.id === selectedInst) || null, [instances, selectedInst]);
  const istProd = istProdNah(instanz?.stage);
  // Das Detail gehoert erst dann zur gewaehlten Kennung, wenn es dieselbe Pid traegt. Ohne
  // diese Bedingung zeigten die Zeilen im Ladefenster noch die alte Kennung, bekaemen aber
  // die neue mitgegeben - ein Klick auf Löschen traefe dann die falsche Kennung.
  const detail = pidDetail?.Pid === selectedPid ? pidDetail : null;
  // Die INSTANZ gehoert zwingend in den Schluessel. Ohne sie traegt derselbe Schluessel auf DEV
  // und PROD - und dieselbe Kennung mit derselben Datei ist dort der Normalfall. Der geladene
  // DEV-Inhalt erschiene dann unter PROD, und eine auf DEV getippte Aenderung koennte still auf
  // PROD landen, sobald beide Staende zufaellig gleich sind (direkt nach einem Transport).
  const schluessel = (id) => `${selectedInst}|${selectedPid}|${id}`;
  const setzeArbeit = useCallback((key, fn) => {
    setArbeit(a => ({ ...a, [key]: fn(a[key] || {}) }));
  }, []);
  const istEntwurf = (a) => a && a.entwurf != null && a.entwurf !== a.inhalt;
  const editorSchmutzig = Object.entries(arbeit)
    .some(([k, a]) => k.startsWith(`${selectedPid}|`) && istEntwurf(a));
  const irgendeinEntwurf = Object.values(arbeit).some(istEntwurf);

  // Alle Instanzen aus allen Spaces sammeln - einmal je spaces-Aenderung, ohne Doppel.
  useEffect(() => {
    let abgebrochen = false;
    Promise.all((spaces || []).map(sp =>
      api(`/api/spaces/${sp.id}/instances`)
        .then(list => (list || []).map(i => ({...i, spaceName: sp.name})))
        .catch(() => [])
    )).then(gruppen => {
      if (abgebrochen) return;
      const flach = gruppen.flat();
      const gesehen = new Set();
      // Sortiert werden DATENNAMEN (Space und Instanz), nicht Beschriftungen. Das Gebietsschema
      // bestimmt hier nur die Reihenfolge von Umlauten und Akzenten und zieht deshalb mit der
      // Sprache mit. Bewusst ueber gebiet() statt ueber eine Abhaengigkeit dieses Effekts: sonst
      // wuerde ein Sprachwechsel alle Instanzen aller Spaces erneut abrufen, nur zum Umsortieren.
      // Die Reihenfolge zieht damit beim naechsten Laden nach.
      setInstances(flach.filter(i => !gesehen.has(i.id) && gesehen.add(i.id))
        .sort((a, b) => `${a.spaceName} ${a.name}`.localeCompare(`${b.spaceName} ${b.name}`, gebiet())));
    });
    return () => { abgebrochen = true; };
  }, [spaces]);

  // still = ohne Skelettzustand. Nach einem Schreibvorgang steht die Liste bereits auf dem
  // Schirm; sie gegen Platzhalter zu tauschen sieht aus, als faenge alles von vorn an.
  const ladePartner = useCallback(async (instId, behalteAuswahl, still) => {
    if (!still) setLoading(true);
    setListFehler(null); setListStatus(null);
    if (!behalteAuswahl) { setPartners([]); setSelPid(null); setPidDetail(null); }
    try {
      const data = await api(`/api/${instId}/partner-directory`);
      const liste = data.partners || [];
      setPartners(liste);
      // Was der Tenant nicht mehr fuehrt, bleibt nicht angehakt: sonst schickt der Dialog
      // Kennungen los, die es nicht gibt - im Extremfall eine leere Liste ins 400.
      const da = new Set(liste.map(p => p.Pid));
      setAuswahl(a => (a.size === 0 ? a : new Set([...a].filter(p => da.has(p)))));
    } catch (e) { setListFehler(e.message); setListStatus(e.status || null); }
    if (!still) setLoading(false);
  }, []);

  const waehleInstanz = (id) => {
    setSelInst(id); setSuche(""); setRecht(null); setWunschPid(null); setWunschFehler(null);
    setPartners([]); setSelPid(null); setPidDetail(null); setListFehler(null); setDetailFehler(null);
    // Die Auswahl gehoert zur Instanz: Kennungen eines anderen Tenants sind kein Vorrat.
    // Mit ihr endet der Modus, und die geholten Zielrechte gelten fuer den alten Space.
    setWaehlend(false); setAuswahl(new Set()); setZielRecht({}); setZielWahlOffen(false); setKopierZiel(null);
    // Geladener Inhalt und Rueckfallkopien der bisherigen Instanz verwerfen - sie gehoeren zu
    // einem anderen Tenant. Offene Entwuerfe bleiben: sie sind ungesicherte Arbeit und ueber
    // dieselbe Instanz wieder erreichbar; der Schluessel traegt die Instanz mit.
    setArbeit(a => Object.fromEntries(Object.entries(a).filter(([, v]) => istEntwurf(v))));
    if (!id) return;
    // Scheitert die Auskunft, bleibt es beim Leserecht - lieber ein fehlender Knopf als
    // ein angebotener, der im 403 endet.
    api(`/api/instances/${id}/permissions`).then(setRecht)
      .catch(() => setRecht({ transport:false, ungeklaert:true }));
    ladePartner(id);
  };

  // Wer selbst umschaltet, hat die Vorauswahl hinter sich gelassen: die anliegenden
  // Eigenschaften gelten als erledigt, sonst zieht der Effekt gleich wieder zurueck.
  const gewaehlteInstanz = instances.find(i => i.id === selectedInst) || null;
  // Nach Space gruppiert, Reihenfolge wie in der Seitenleiste. Ein Space ohne Instanzen faellt
  // heraus - eine leere Ueberschrift beantwortet keine Frage.
  const nachSpace = useMemo(() => {
    const m = new Map();
    for (const i of instances) {
      if (!m.has(i.spaceName)) m.set(i.spaceName, []);
      m.get(i.spaceName).push(i);
    }
    return [...m.entries()];
  }, [instances]);

  const waehleInstanzManuell = (id) => {
    letzteVorauswahl.current = { inst: preselectedInstId, pid: preselectedPid };
    waehleInstanz(id);
  };

  // Vorauswahl aus dem Ereignis cpi-nav-pd (Sprung aus der Pipeline-Topologie).
  // Bewusst ueber ein Merkmal der zuletzt umgesetzten Vorauswahl statt ueber "noch nichts
  // gewaehlt": sonst laeuft jeder zweite Sprung ins Leere, solange die Seite offen bleibt.
  useEffect(() => {
    if (!preselectedInstId && !preselectedPid) return;
    const zuletzt = letzteVorauswahl.current;
    if (zuletzt.inst === preselectedInstId && zuletzt.pid === preselectedPid) return;
    setWunschFehler(null);

    if (preselectedInstId && preselectedInstId !== selectedInst) {
      if (instances.length === 0) return;   // Instanzen sind noch unterwegs, Effekt greift erneut
      letzteVorauswahl.current = { inst: preselectedInstId, pid: preselectedPid };
      if (!instances.some(i => i.id === preselectedInstId)) {
        setWunschFehler(t("meldung.instanzFehlt"));
        return;
      }
      waehleInstanz(preselectedInstId);
    } else {
      letzteVorauswahl.current = { inst: preselectedInstId, pid: preselectedPid };
    }

    if (preselectedPid) { setSuche(""); setWunschPid(preselectedPid); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preselectedInstId, preselectedPid, instances, selectedInst]);

  // Gewuenschte Kennung aufloesen, sobald die Liste steht. Fehlt sie, wird das gesagt -
  // stilles Nichtstun laesst den Absprung aus der Topologie wie einen Fehler aussehen.
  useEffect(() => {
    if (!wunschPid || !selectedInst || loading) return;
    if (partners.some(p => p.Pid === wunschPid)) setSelPid(wunschPid);
    else if (!listFehler) setWunschFehler(t("meldung.pidFehlt", { pid: wunschPid }));
    setWunschPid(null);
  }, [wunschPid, partners, loading, selectedInst, listFehler, t]);

  // Jede Kennung beginnt beim ersten Reiter - sonst landet man auf einem Reiter,
  // den die vorige Kennung hatte und diese nicht.
  useEffect(() => { setActiveTab("string"); }, [selectedPid]);

  // Detail entprellt nachladen - so kostet die Pfeiltasten-Navigation keine Tenant-Last.
  useEffect(() => {
    if (!selectedInst || !selectedPid) { setPidDetail(null); return undefined; }
    const lauf = ++detailLauf.current;
    setPidLaedt(true); setDetailFehler(null);
    const t = setTimeout(async () => {
      try {
        const data = await api(`/api/${selectedInst}/partner-directory/${encodeURIComponent(selectedPid)}`);
        if (detailLauf.current !== lauf) return;
        setPidDetail(data);
      } catch (e) {
        if (detailLauf.current !== lauf) return;
        setPidDetail(null); setDetailFehler(e.message);
      }
      if (detailLauf.current === lauf) setPidLaedt(false);
    }, 160);
    return () => clearTimeout(t);
  }, [selectedInst, selectedPid]);

  const neuLaden = useCallback(async () => {
    setDetailFehler(null);
    const pid = selectedPid;
    await ladePartner(selectedInst, true, true);
    if (!pid) return;
    const lauf = ++detailLauf.current;
    try {
      const data = await api(`/api/${selectedInst}/partner-directory/${encodeURIComponent(pid)}`);
      if (detailLauf.current !== lauf) return;
      const leer = !data.stringParams.length && !data.binaryParams.length && !data.altPartners.length;
      if (leer) { setSelPid(null); setPidDetail(null); } else setPidDetail(data);
    } catch (e) { setDetailFehler(e.message); }
  }, [selectedInst, selectedPid, ladePartner]);

  // Ungespeicherte Bearbeitung soll nicht still verloren gehen - unabhaengig davon, zu
  // welcher Kennung sie gehoert und ob ihr Reiter gerade sichtbar ist.
  useEffect(() => {
    if (!irgendeinEntwurf) return undefined;
    const h = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [irgendeinEntwurf]);

  const gefiltert = useMemo(() => {
    const q = suche.trim().toLowerCase();
    return q ? partners.filter(p => p.Pid.toLowerCase().includes(q)) : partners;
  }, [partners, suche]);

  // Die gewaehlte Zeile muss sichtbar sein - bei mehreren hundert Kennungen liegt sie
  // nach einem Sprung aus der Topologie sonst weit ausserhalb des Ausschnitts.
  useEffect(() => {
    if (!selectedPid || !rowsRef.current) return;
    const zeile = [...rowsRef.current.querySelectorAll(".pd-row")].find(k => k.dataset.pid === selectedPid);
    zeile?.scrollIntoView({ block:"nearest" });
  }, [selectedPid, gefiltert]);

  const xsltAnzahl = useMemo(() => partners.filter(p => p.binaryParams.length > 0).length, [partners]);

  /* ── Kopieren: Auswahlmodus, Ziele, Sperrregel ────────────────────────────── */

  const nichtKopierbareDa = useMemo(() => partners.some(p => !KOPIERBAR(p)), [partners]);

  // Ziele sind ausschliesslich Instanzen desselben BESITZENDEN Spaces. spaceId zeigt auch bei
  // gespiegelten Freigaben auf den Ursprung; die Ueberschrift im Popup taete das nicht - unter
  // ihr koennen Instanzen mehrerer echter Spaces stehen.
  const moeglicheZiele = useMemo(() => gewaehlteInstanz
    ? instances.filter(i => i.id !== gewaehlteInstanz.id && i.spaceId === gewaehlteInstanz.spaceId)
    : [], [instances, gewaehlteInstanz]);
  const quelleSpaceName = useMemo(() =>
    (spaces || []).find(s => s.id === gewaehlteInstanz?.spaceId)?.name
      || gewaehlteInstanz?.spaceName || "",
    [spaces, gewaehlteInstanz]);
  const kopierGrund = moeglicheZiele.length === 0 ? t("ctx.keinZiel") : null;

  const starteAuswahl = () => {
    setWaehlend(true);
    // Wer einen Eintrag offen hat und kopieren drueckt, meint fast immer genau diesen.
    setAuswahl(new Set(KOPIERBAR(partners.find(p => p.Pid === selectedPid)) ? [selectedPid] : []));
    // Die Rechte am Ziel laufen los, waehrend noch Haken gesetzt werden - das Popup oeffnet
    // damit in seinem Endzustand, statt Zeilen unter dem Zeiger umzuschalten.
    moeglicheZiele.forEach(i => {
      api(`/api/instances/${i.id}/permissions`)
        .then(r => setZielRecht(z => ({ ...z, [i.id]: r?.transport === true })))
        .catch(() => setZielRecht(z => ({ ...z, [i.id]: false })));
    });
  };
  // Das Leeren der Auswahl beendet den Modus NICHT - ein Modus, der unter der Hand
  // verschwindet, ist schlimmer als einer, den man schliessen muss.
  const beendeAuswahl = useCallback(() => { setWaehlend(false); setAuswahl(new Set()); }, []);

  const hakeUm = (pid) => setAuswahl(a => {
    const n = new Set(a); if (n.has(pid)) n.delete(pid); else n.add(pid); return n;
  });

  const sichtbarKopierbar = useMemo(() => gefiltert.filter(p => KOPIERBAR(p)), [gefiltert]);
  const alleSichtbarAn = sichtbarKopierbar.length > 0 && sichtbarKopierbar.every(p => auswahl.has(p.Pid));
  const einigeSichtbarAn = sichtbarKopierbar.some(p => auswahl.has(p.Pid));
  const alleUmschalten = () => setAuswahl(a => {
    const n = new Set(a);
    sichtbarKopierbar.forEach(p => { if (alleSichtbarAn) n.delete(p.Pid); else n.add(p.Pid); });
    return n;
  });
  const versteckteAuswahl = useMemo(() => {
    const sichtbar = new Set(gefiltert.map(p => p.Pid));
    return [...auswahl].filter(p => !sichtbar.has(p)).length;
  }, [auswahl, gefiltert]);

  // Reihenfolge der Liste, nicht die des Anklickens - so liest der Dialog sich wie die Liste.
  const gewaehltePids = useMemo(() => partners.map(p => p.Pid).filter(p => auswahl.has(p)),
    [partners, auswahl]);

  // Offene Entwuerfe zu gewaehlten Kennungen: der Dialog muss sagen, dass der GESPEICHERTE
  // Stand kopiert wird - sonst stehen zwei Wahrheiten nebeneinander.
  const entwurfPids = useMemo(() => {
    const s = new Set();
    for (const [k, a] of Object.entries(arbeit)) {
      if (!(a && a.entwurf != null && a.entwurf !== a.inhalt)) continue;
      const t = k.split("|");
      if (t[0] === selectedInst && auswahl.has(t[1])) s.add(t[1]);
    }
    return [...s];
  }, [arbeit, selectedInst, auswahl]);

  const zielSperre = useCallback((i) => {
    const q = gewaehlteInstanz;
    if (!q) return null;
    if (i.id === q.id)             return { kurz:t("zielwahl.quelleKurz"), satz:t("zielwahl.quelleSatz") };
    if (i.spaceId !== q.spaceId)   return { kurz:t("zielwahl.andererSpace"), satz:null };
    if (i.credsIncomplete)         return { kurz:t("zielwahl.credsKurz"), satz:t("zielwahl.credsSatz") };
    if (zielRecht[i.id] === false) return { kurz:t("zielwahl.keinRechtKurz"), satz:t("zielwahl.keinRechtSatz") };
    if (zielRecht[i.id] !== true)  return { kurz:t("zielwahl.wirdGeprueft"), satz:null };
    return null;
  }, [gewaehlteInstanz, zielRecht, t]);

  const zielGruppeSperre = useCallback((space, liste) =>
    gewaehlteInstanz && liste.every(i => i.spaceId !== gewaehlteInstanz.spaceId)
      ? t("zielwahl.gruppeGesperrt", { space: quelleSpaceName })
      : null,
    [gewaehlteInstanz, quelleSpaceName, t]);

  // Escape ist das Tastatur-Gegenstueck zu Abbrechen, kein zweites Bedienelement. Es darf
  // deshalb NUR wirken, wenn die Taste sonst niemandem gehoert: nicht bei offenem Dialog und
  // nicht aus einem Eingabefeld heraus - dort verwirft Escape die Eingabe, und die Auswahl
  // gleich mit wegzuwerfen waere eine zweite, unsichtbare Wirkung derselben Taste.
  useEffect(() => {
    if (!waehlend || zielWahlOffen || kopierZiel || anlegen || instWahlOffen || wechselFrage) return undefined;
    const h = (e) => {
      if (e.key !== "Escape") return;
      if (e.target?.closest?.("input,textarea,.iio-modal")) return;
      beendeAuswahl();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [waehlend, zielWahlOffen, kopierZiel, anlegen, instWahlOffen, wechselFrage, beendeAuswahl]);

  const sichtbareReiter = useMemo(() => {
    if (!detail) return [];
    // Leere Reiter ausblenden statt ausgrauen. Mit Schreibrecht bleibt ein leerer Reiter
    // stehen, weil man dort den ersten Inhalt anlegt - "Benutzer" nicht, dort gibt es
    // keinen Schreibweg (SAP pflegt die Zuordnung im Cockpit).
    return REITER.filter(r => r.n(detail) > 0 || (darfSchreiben && r.k !== "users"));
  }, [detail, darfSchreiben]);

  useEffect(() => {
    if (sichtbareReiter.length === 0) return;
    if (!sichtbareReiter.some(r => r.k === activeTab)) setActiveTab(sichtbareReiter[0].k);
  }, [sichtbareReiter, activeTab]);

  const pfeiltasten = (e) => {
    const tasten = ["ArrowDown", "ArrowUp", "Home", "End"];
    if (!tasten.includes(e.key)) return;
    e.preventDefault();
    const knoepfe = [...(rowsRef.current?.querySelectorAll(".pd-row") || [])];
    if (knoepfe.length === 0) return;
    const jetzt = knoepfe.findIndex(k => k.dataset.pid === selectedPid);
    let ziel = 0;
    if (e.key === "ArrowDown") ziel = jetzt < 0 ? 0 : Math.min(knoepfe.length - 1, jetzt + 1);
    else if (e.key === "ArrowUp") ziel = jetzt < 0 ? knoepfe.length - 1 : Math.max(0, jetzt - 1);
    else if (e.key === "End") ziel = knoepfe.length - 1;
    knoepfe[ziel].focus();
    setSelPid(knoepfe[ziel].dataset.pid);
  };

  const inhaltAnzahl = detail
    ? detail.stringParams.length + detail.binaryParams.length + detail.altPartners.length
    : 0;

  // Anlegen bei aktiver Suche: der neue Eintrag muss danach sichtbar sein, sonst steht
  // links "Kein Treffer" auf genau das, was gerade entstanden ist.
  const oeffneAnlegen = (art) => { setSuche(""); setAnlegen(art); };

  return (
    <div className="pd-root">
      <style>{PD_CSS}</style>

      <div className="pd-ph">
        <div className="crumb">{t("kopf.brotkrume")} <b>&gt;&gt;</b> {t("kopf.brotkrumeAktuell")}</div>
        <h1>{t("kopf.titel")}</h1>
        <div className="sub">{t("kopf.einleitung")}</div>
      </div>

      <div className="pd-ctx">
        {/* Ein Knopf, der die getroffene Wahl AUSSCHREIBT - Space obendrueber, Instanz gross.
            Die frueherer Auswahlliste presste beides in eine Zeile "Space / Instanz" und war bei
            vielen Instanzen unuebersichtlich. Der Knopf ist der einzige Weg zur Wahl; die Liste
            steckt im Popup, es gibt sie nicht doppelt. */}
        <div className="pd-field">
          <span className="pd-lbl" id="pd-inst-lbl">{t("ctx.instanz")}</span>
          <button type="button" className={"pd-instbtn" + (gewaehlteInstanz ? " gewaehlt" : "")}
            aria-labelledby="pd-inst-lbl" aria-haspopup="dialog" aria-expanded={instWahlOffen}
            onClick={() => setInstWahlOffen(true)}>
            {gewaehlteInstanz ? (<>
              <span className="txt">
                <span className="sp">{gewaehlteInstanz.spaceName}</span>
                <span className="nm">{gewaehlteInstanz.name}</span>
              </span>
              <span className="wechseln">{t("ctx.wechseln")}</span>
            </>) : (<>
              <span className="txt"><span className="nm leer">{t("instanzwahl.titel")}</span></span>
              <svg viewBox="0 0 24 24" aria-hidden="true"><polyline points="6 9 12 15 18 9" /></svg>
            </>)}
          </button>
        </div>

        {selectedInst && !loading && !listFehler && (
          <div className="pd-ctx-count">
            {/* Bei aktiver Suche zeigt die Kopfzahl dasselbe wie die Liste darunter. */}
            <span>
              {gefiltert.length === partners.length
                ? <Trans t={t} i18nKey="ctx.bestand" count={partners.length}
                    values={{ count: partners.length }} components={{ b: <b/> }} />
                : <Trans t={t} i18nKey="ctx.bestandGefiltert"
                    values={{ n: gefiltert.length, gesamt: partners.length }} components={{ b: <b/> }} />}
            </span>
            {xsltAnzahl > 0 && <>
              <span className="sep" aria-hidden="true" />
              <span><Trans t={t} i18nKey="ctx.mitXslt" values={{ count: xsltAnzahl }} components={{ b: <b/> }} /></span>
            </>}
          </div>
        )}

        <div className="pd-ctx-sp" />
        {/* EIN Knopf an EINER Stelle, der die Handlung fortschreibt statt sie zu verdoppeln:
            "Einträge kopieren" schaltet den Auswahlmodus ein, im Modus steht an derselben
            Stelle die Ausloesung. Kopieren liest in der Quelle und schreibt im Ziel - ein
            fehlendes Schreibrecht auf der QUELLE verbirgt den Knopf deshalb nicht. */}
        {selectedInst && !waehlend && <>
          {kopierGrund && <div className="pd-ctx-grund" id="pd-kop-grund">{kopierGrund}</div>}
          <button type="button" className="pd-btn ghost" onClick={starteAuswahl} disabled={!!kopierGrund}
            aria-describedby={kopierGrund ? "pd-kop-grund" : undefined}>
            <IcoKopie/>{t("ctx.kopierenStarten")}
          </button>
        </>}

        {selectedInst && waehlend && <>
          <button type="button" className="pd-btn ghost" onClick={beendeAuswahl}>{t("gemeinsam:aktion.abbrechen")}</button>
          {/* Dieselbe Zahl wie im Dialog: beide zaehlen die Kennungen, die es in der Liste
              wirklich gibt - nicht die Haken, die einmal gesetzt wurden. */}
          <button type="button" className="pd-btn prim" disabled={gewaehltePids.length === 0}
            onClick={() => setZielWahlOffen(true)}>
            {gewaehltePids.length === 0 ? t("ctx.kopierenStarten")
              : t("ctx.kopierenAusloesen", { count: gewaehltePids.length })}
          </button>
        </>}

        {selectedInst && !waehlend && (darfSchreiben
          ? <button type="button" className="pd-btn prim" onClick={() => oeffneAnlegen("eintrag")}>
              <IcoPlus/>{t("ctx.eintragAnlegen")}
            </button>
          : <div className="pd-ctx-ro">
              {!recht ? t("ctx.rechtWirdGeprueft")
                : recht.ungeklaert ? t("ctx.rechtUngeklaert")
                : t("ctx.nurLesen")}
            </div>
        )}
      </div>

      {listFehler && (
        <div className="pd-msg bad">
          {/* Ueber den STATUS pruefen, nicht ueber den Text: seit der Filter in
              backend/lib/meldungen.js die Servermeldungen uebersetzt, ist der
              Wortlaut sprachabhaengig. "Endpoint nicht verfügbar auf diesem
              Tenant" kommt auf Englisch als "Endpoint not available on this
              tenant" an, und ein Textvergleich griffe dann nicht mehr.
              Der Rueckfall auf "501" faengt Faelle ohne durchgereichten Status. */}
          {[404, 501].includes(listStatus) || listFehler.includes("501")
            ? t("meldung.pdNichtVerfuegbar")
            : listFehler}
        </div>
      )}

      {wunschFehler && (
        <div className="pd-msg warn">
          <span>{wunschFehler}</span>
          <button type="button" className="pd-btn ghost sm re" onClick={() => setWunschFehler(null)}>{t("meldung.ausblenden")}</button>
        </div>
      )}

      {!selectedInst && (
        <div className="pd-msg">{t("meldung.instanzWaehlen")}</div>
      )}

      {selectedInst && (
        <div className="pd-split">
          {/* Liste */}
          <div className="pd-list">
            <div className="pd-search">
              <svg viewBox="0 0 24 24"><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/></svg>
              <input value={suche} onChange={e => setSuche(e.target.value)}
                placeholder={t("liste.suchen")} aria-label={t("liste.suchen")} />
            </div>

            {/* Die Zeile erscheint nur, wenn sie etwas zu sagen hat - bei leerer Liste und bei
                "Kein Treffer" ohne verdeckte Auswahl waere sie ein Bedienelement ohne Gegenstand. */}
            {waehlend && (sichtbarKopierbar.length > 0 || versteckteAuswahl > 0) && (
              <div className="pd-selbar">
                {sichtbarKopierbar.length > 0 && (
                  <button type="button" className="alle" onClick={alleUmschalten}>
                    <span className={"pd-kbox" + (alleSichtbarAn ? " an" : einigeSichtbarAn ? " teil" : "")}
                      aria-hidden="true" />
                    {/* Bei aktiver Suche greift der Knopf nur auf die Treffer - dann sagt er das auch. */}
                    {suche.trim()
                      ? (alleSichtbarAn ? t("liste.trefferAbwaehlen") : t("liste.trefferAuswaehlen"))
                      : (alleSichtbarAn ? t("liste.auswahlAufheben") : t("liste.alleAuswaehlen"))}
                  </button>
                )}
                {versteckteAuswahl > 0 && (
                  <span className="versteckt">
                    {t("liste.versteckt", { count: versteckteAuswahl })}
                  </span>
                )}
              </div>
            )}
            {waehlend && nichtKopierbareDa && (
              <div className="pd-selnote">
                {partners.some(p => p.systemGrund)
                  ? t("liste.systemeigen")
                  : t("liste.sonderzeichen")}
              </div>
            )}

            <div className={"pd-rows" + (waehlend ? " waehlend" : "")} ref={rowsRef} onKeyDown={pfeiltasten}
              role={waehlend ? "group" : undefined}
              aria-label={waehlend ? t("liste.ariaAuswahl") : undefined}>
              {loading && [...Array(6)].map((_, i) => <div key={i} className="pd-skel" />)}

              {!loading && gefiltert.length === 0 && (
                <div className="pd-list-empty">
                  {partners.length === 0
                    ? <><b>{t("liste.leerTitel")}</b>{t("liste.leerText")}</>
                    : <><b>{t("liste.keinTrefferTitel")}</b>{t("liste.keinTrefferText", { suche })}</>}
                </div>
              )}

              {!loading && gefiltert.map(p => {
                const teile = [];
                if (p.stringParams.length) teile.push(
                  <span key="p">{t("liste.nParameter", { count: p.stringParams.length })}</span>);
                if (p.binaryParams.length) teile.push(
                  <span key="x" className="xslt">{t("liste.nXslt", { count: p.binaryParams.length })}</span>);
                if (p.altPartners.length) teile.push(
                  <span key="k">{t("liste.nKennungen", { count: p.altPartners.length })}</span>);
                const kann = KOPIERBAR(p);
                const an = auswahl.has(p.Pid);
                return (
                  <button key={p.Pid} type="button" data-pid={p.Pid}
                    className={"pd-row" + (selectedPid === p.Pid ? " is-active" : "")
                      + (waehlend && !kann ? " nichtkopierbar" : "")
                      + (waehlend && an ? " gewaehlt" : "")}
                    aria-current={selectedPid === p.Pid ? "true" : undefined}
                    role={waehlend && kann ? "checkbox" : undefined}
                    aria-checked={waehlend && kann ? an : undefined}
                    aria-disabled={waehlend && !kann ? "true" : undefined}
                    onClick={() => {
                      if (!waehlend) { setSelPid(p.Pid); return; }
                      if (!kann) return;   // Der Grund steht in der Zeile, kein Ruetteln
                      // Ein Klick tut genau eine Sache: der Haken wechselt. Dass die beruehrte
                      // Zeile rechts erscheint, ist Folge des Fokus, nicht ein zweites Ziel.
                      hakeUm(p.Pid);
                      setSelPid(p.Pid);
                    }}>
                    {waehlend && (kann
                      ? <span className={"pd-kbox" + (an ? " an" : "")} aria-hidden="true" />
                      : <span aria-hidden="true" />)}
                    <span className="txt">
                      <span className="pid" title={p.Pid}>{p.Pid}</span>
                      <span className="inh">
                        {teile.length === 0
                          ? t("liste.ohneInhalt")
                          : teile.reduce((acc, el, i) => i === 0 ? [el]
                              : [...acc, <span key={"s"+i} className="sep">·</span>, el], [])}
                      </span>
                    </span>
                    {/* Der Grund steht sichtbar in der Zeile, nicht nur im Titel: "ausgegraut ohne
                        Begruendung" war hier schon einmal ein Befund. */}
                    {waehlend && !kann && (
                      <span className="nk" title={p.systemGrund || undefined}>
                        {p.systemGrund ? t("liste.nkSystem") : t("liste.nkAllgemein")}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Detail */}
          <div className="pd-detail">
            {waehlend && (
              <div className="pd-ruhe"><span className="bar" />{t("detail.ruht")}</div>
            )}
            {!selectedPid && (
              <div className="pd-detail-leer">
                <b>{t("detail.leerTitel")}</b>
                <span>{t("detail.leerText")}</span>
              </div>
            )}

            {selectedPid && <>
              <div className="pd-dhead">
                <div className="pd-dhead-copy">
                  <div className="eyebrow">{t("detail.eyebrow")}</div>
                  <h2 title={selectedPid}>{selectedPid}</h2>
                </div>
                <PartnerDirectoryEdgeSync key={`${selectedInst}|${selectedPid}`} instanceId={selectedInst}
                  instanceName={instanz?.name || ""} pid={selectedPid}
                  blocked={!detail || !darfBearbeiten || irgendeinEntwurf || !!anlegen || !!partners.find(p => p.Pid === selectedPid)?.systemGrund}
                  blockedReason={!detail ? "checking" : !darfSchreiben ? "reasons.EDGE_PERMISSION_REQUIRED"
                    : partners.find(p => p.Pid === selectedPid)?.systemGrund ? "reasons.EDGE_PID_INVALID" : waehlend ? "finishSelection" : "saveFirst"}/>
              </div>

              {detail && sichtbareReiter.length > 0 && (
                <div className="pd-tabs" role="tablist">
                  {sichtbareReiter.map(r => (
                    <button key={r.k} type="button" role="tab" aria-selected={activeTab === r.k}
                      className={"pd-tab" + (activeTab === r.k ? " is-active" : "")}
                      onClick={() => setActiveTab(r.k)}>
                      {t(r.l)}<span className="n">{r.n(detail)}</span>
                      {r.k === "binary" && editorSchmutzig && (
                        <span className="offen" aria-label={t("detail.offeneAenderung")} />
                      )}
                    </button>
                  ))}
                </div>
              )}

              <div className="pd-dbody">
                {detailFehler && (
                  <div className="pd-msg bad">
                    <span>{detailFehler}</span>
                    <button type="button" className="pd-btn ghost sm re" onClick={neuLaden}>{t("detail.erneutVersuchen")}</button>
                  </div>
                )}

                {pidLaedt && !detail && <div className="pd-empty">{t("detail.laedt")}</div>}

                {detail && activeTab === "string" && (
                  detail.stringParams.length === 0 ? (
                    <div className="pd-empty">
                      <b>{t("detail.leerString")}</b>
                      {t("detail.leerStringText")}
                      {darfBearbeiten && (
                        <button type="button" className="pd-add" onClick={() => oeffneAnlegen("param")}>
                          <IcoPlus/>{t("detail.paramHinzufuegen")}
                        </button>
                      )}
                    </div>
                  ) : <>
                    <div className="pd-tbl">
                      <div className="pd-tr head">
                        <span>{t("detail.spalteParameter")}</span><span>{t("detail.spalteWert")}</span><span />
                      </div>
                      {/* Die Kennung gehoert in den Schluessel: sonst wandern offenes Eingabefeld
                          und offene Lösch-Rückfrage beim Wechsel auf die naechste Kennung. */}
                      {detail.stringParams.map(p => (
                        <PdStringZeile key={schluessel(p.Id)} param={p} pid={selectedPid} instId={selectedInst}
                          darfSchreiben={darfBearbeiten} letzterInhalt={inhaltAnzahl === 1} ruht={waehlend}
                          aufAenderung={neuLaden} aufFehler={setDetailFehler} />
                      ))}
                    </div>
                    {darfBearbeiten && (
                      <button type="button" className="pd-add" onClick={() => oeffneAnlegen("param")}>
                        <IcoPlus/>{t("detail.paramHinzufuegen")}
                      </button>
                    )}
                  </>
                )}

                {detail && activeTab === "binary" && (
                  detail.binaryParams.length === 0 ? (
                    <div className="pd-empty">
                      <b>{t("detail.leerDatei")}</b>
                      {t("detail.leerDateiText")}
                      {darfBearbeiten && (
                        <button type="button" className="pd-add" onClick={() => oeffneAnlegen("datei")}>
                          <IcoPlus/>{t("detail.dateiHinzufuegen")}
                        </button>
                      )}
                    </div>
                  ) : <>
                    {detail.binaryParams.map(p => (
                      <PdDatei key={schluessel(p.Id)} param={p} pid={selectedPid} instId={selectedInst}
                        darfSchreiben={darfBearbeiten} istProd={istProd} letzterInhalt={inhaltAnzahl === 1}
                        ruht={waehlend}
                        stand={arbeit[schluessel(p.Id)]}
                        setzeStand={fn => setzeArbeit(schluessel(p.Id), fn)}
                        aufAenderung={neuLaden} aufFehler={setDetailFehler} />
                    ))}
                    {darfBearbeiten && (
                      <button type="button" className="pd-add" onClick={() => oeffneAnlegen("datei")}>
                        <IcoPlus/>{t("detail.dateiHinzufuegen")}
                      </button>
                    )}
                  </>
                )}

                {detail && activeTab === "alt" && (
                  detail.altPartners.length === 0 ? (
                    <div className="pd-empty">
                      <b>{t("detail.leerAlt")}</b>
                      {t("detail.leerAltText")}
                      {darfBearbeiten && (
                        <button type="button" className="pd-add" onClick={() => oeffneAnlegen("alt")}>
                          <IcoPlus/>{t("detail.altHinzufuegen")}
                        </button>
                      )}
                    </div>
                  ) : <>
                    <div className="pd-tbl">
                      <div className="pd-tr head cols4">
                        <span>{t("detail.spalteAgentur")}</span><span>{t("detail.spalteSchema")}</span>
                        <span>{t("detail.spalteKennung")}</span><span />
                      </div>
                      {detail.altPartners.map(p => (
                        <PdAltZeile key={`${p.Agency}|${p.Scheme}|${p.Id}`} eintrag={p} instId={selectedInst}
                          darfSchreiben={darfBearbeiten} letzterInhalt={inhaltAnzahl === 1} ruht={waehlend}
                          aufAenderung={neuLaden} aufFehler={setDetailFehler} />
                      ))}
                    </div>
                    {darfBearbeiten && (
                      <button type="button" className="pd-add" onClick={() => oeffneAnlegen("alt")}>
                        <IcoPlus/>{t("detail.altHinzufuegen")}
                      </button>
                    )}
                    {darfBearbeiten && (
                      <div className="pd-fuss">{t("detail.altUnveraenderlich")}</div>
                    )}
                  </>
                )}

                {detail && activeTab === "users" && (
                  <>
                    <div className="pd-tbl">
                      <div className="pd-tr head cols1"><span>{t("detail.spalteBenutzer")}</span></div>
                      {detail.authorizedUsers.map(u => (
                        <div key={u.User} className="pd-tr cols1"><span className="val">{u.User}</span></div>
                      ))}
                    </div>
                    <div className="pd-fuss">{t("detail.benutzerFuss")}</div>
                  </>
                )}
              </div>
            </>}
          </div>
        </div>
      )}

      {anlegen && (
        <PdAnlegen art={anlegen} pid={selectedPid} instId={selectedInst}
          vorhandenePids={partners.map(p => p.Pid)}
          aufAbbruch={() => setAnlegen(null)}
          aufFertig={async (pid) => {
            setAnlegen(null);
            if (pid !== selectedPid) { await ladePartner(selectedInst, true, true); setSelPid(pid); }
            else await neuLaden();
          }} />
      )}

      {instWahlOffen && (
        <PdInstanzWahl
          gruppen={nachSpace}
          aktuell={selectedInst}
          aufWahl={(id) => {
            setInstWahlOffen(false);
            if (id === selectedInst) return;
            if (waehlend && gewaehltePids.length > 1) { setWechselFrage(id); return; }
            waehleInstanzManuell(id);
          }}
          aufAbbruch={() => setInstWahlOffen(false)} />
      )}

      {wechselFrage && (
        <PdWechselFrage
          anzahl={gewaehltePids.length}
          quelleName={gewaehlteInstanz?.name || ""}
          zielName={instances.find(i => i.id === wechselFrage)?.name || ""}
          aufBleiben={() => setWechselFrage(null)}
          aufWechseln={() => { const id = wechselFrage; setWechselFrage(null); waehleInstanzManuell(id); }} />
      )}

      {/* Dieselbe Komponente, andere Frage: die Liste der Instanzen gibt es nur einmal. */}
      {zielWahlOffen && gewaehlteInstanz && (
        <PdInstanzWahl
          gruppen={nachSpace}
          aktuell={selectedInst}
          eyebrow={t("kopieren.eyebrow")}
          titel={t("kopieren.zielTitel")}
          gesperrt={zielSperre}
          gruppeGesperrt={zielGruppeSperre}
          aufWahl={(id) => {
            setZielWahlOffen(false);
            setKopierZiel(instances.find(i => i.id === id) || null);
          }}
          aufAbbruch={() => setZielWahlOffen(false)} />
      )}

      {kopierZiel && gewaehlteInstanz && (
        <PdKopierDialog
          quelle={gewaehlteInstanz}
          ziel={kopierZiel}
          pids={gewaehltePids}
          entwurfPids={entwurfPids}
          aufSchliessen={(sauber) => {
            setKopierZiel(null);
            // Nach vollstaendigem Erfolg ist der Kontext verbraucht, der Modus endet. Nach einem
            // Teilfehler bleibt die Auswahl stehen - sie neu zu setzen waere doppelte Arbeit.
            if (sauber) beendeAuswahl();
          }}
          aufZielOeffnen={() => {
            const zielId = kopierZiel.id;
            setKopierZiel(null); beendeAuswahl();
            waehleInstanzManuell(zielId);
          }} />
      )}
    </div>
  );
};

export default PartnerDirectory;
